/**
 * Package form component for creating and editing BSP packages.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Form, Input, Button, Space, Divider, message, Card, Radio, Modal, Select, Tooltip, Typography } from 'antd';

const { Text } = Typography;
import { SaveOutlined, CloseOutlined, QuestionCircleOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import { MultiFileUpload } from '@/components/common/MultiFileUpload';
import { getProjectFiles, updatePackageFile, deletePackageFile, deleteReleaseNote, completePackageFileUpload, failPackageFileUpload, startPackageFileUpload, heartbeatPackageFile, updatePackageUploadStatus, updatePackage, patchPackage, failReleaseNoteUpload, failTestReportUpload } from '@/api/packages';
import { listExternalReleases } from '@/api/externalRelease';
import { updateUploadRecord } from '@/api/uploadRecords';
import type { UploadedFileItem } from '@/api/uploadRecords';
import { uploadFile } from '@/services/obsService';
import { reportUploadEvent } from '@/api/storage';
import { useUploadProgressStore } from '@/store/uploadProgressStore';
import { useT } from '@/i18n';
import type { BSPPackageDetail, DocAttachment, Project, FileType, ProjectFile, PreviousGitSource } from '@/types';

const { TextArea } = Input;

interface UploadedFile {
  objectKey: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  md5?: string;  // Optional MD5 hash
}

interface PackageFormProps {
  initialData?: BSPPackageDetail | null;
  projects: Project[];
  projectId?: number;
  fileTypes: FileType[];
  onSubmit: (data: PackageFormData) => Promise<{ id: number; version?: number; uploadRecordId?: number; categorizedFiles?: { deleted: any[]; existing: any[] }; files?: Array<{ id: number; file_type_id?: number; file_type?: { id: number }; file_name?: string; upload_status?: string }> }>;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
  previousGitSources?: PreviousGitSource[];
}

export interface PackageFormData {
  name: string; // 底软包名称
  vehicle_model: string;
  vehicle_subtype?: string;
  platform?: string;
  vehicle_models?: string[];
  vehicle_subtypes?: string[];
  platforms?: string[];
  description?: string;
  // 供应商文档
  release_note_url?: string;
  release_note_name?: string;
  release_note_size?: number;
  release_note_checksum?: string;  // MD5 checksum
  release_note_content?: string; // 直接输入的内容
  test_report_url?: string;
  test_report_name?: string;
  test_report_size?: number;
  test_report_checksum?: string;  // MD5 checksum
  test_report_content?: string; // 直接输入的内容
  // Momenta文档
  momenta_release_note_url?: string;
  momenta_release_note_name?: string;
  momenta_release_note_size?: number;
  momenta_release_note_checksum?: string;
  momenta_release_note_content?: string;
  momenta_test_report_url?: string;
  momenta_test_report_name?: string;
  momenta_test_report_size?: number;
  momenta_test_report_checksum?: string;
  momenta_test_report_content?: string;
  files: Array<{
    file_type_id: number;
    file_name: string;
    file_url?: string;
    file_size?: number;
    object_key?: string;
    checksum?: string;
    source_type?: 'upload' | 'select' | 'git' | 'artifact';
    git_url?: string;
    git_branch?: string;
    git_commit?: string;
    git_tag?: string;
    artifact_name?: string;
    file_source: 'supplier' | 'momenta';  // 文件来源（必填）
    upload_status?: 'queued' | 'uploading' | 'done';  // queued = 延迟上传（待上传）, done = 已上传
    remark?: string;  // 文件级备注（选填）
    rawFile?: RcFile;  // only present for deferred files, not sent to API
  }>;
  version?: number; // For optimistic locking in edit mode
  upload_mode?: string; // Upload mode: 'auto' | 'direct' | 'proxy'
  chunk_size?: number; // Chunk size in bytes
  external_release_ids?: number[];
  deleted_file_ids?: number[];   // IDs of existing PackageFiles to delete as part of this update
  deleted_doc_types?: string[];  // Doc types to delete ('release_note', 'test_report')
  test_report_files?: DocAttachment[];  // 测试报告多附件
  doc_files_merge?: boolean;     // 上传回写：后端合并文档列表
}

interface UploadedFileWithType extends UploadedFile {
  fileTypeId: number;
  fileId?: number; // PackageFile ID (if existing file)
  md5?: string; // MD5 hash (inherited from UploadedFile)
  sourceType?: 'upload' | 'select' | 'git' | 'external'; // File source type
  file_source?: 'supplier' | 'momenta'; // File owner (supplier or momenta)
  gitUrl?: string;
  gitBranch?: string;
  gitCommit?: string;
  gitTag?: string;
  remark?: string;  // 文件级备注（选填）
  rawFile?: RcFile;  // present for deferred (not-yet-uploaded) files
  uploadKey?: string;  // progress store key, e.g. "package-1-file-23"
  uploadStatus?: 'queued' | 'uploading' | 'done' | 'failed';
}

interface FormFiles {
  // 支持单文件或多文件数组，根据 FileType.allow_multiple_files 配置动态决定
  [key: string]: UploadedFile | UploadedFileWithType | UploadedFileWithType[] | UploadedFile[] | undefined;
  // 供应商文档
  release_note?: UploadedFile | UploadedFile[] | UploadedFileWithType | UploadedFileWithType[];
  test_report?: UploadedFile | UploadedFile[] | UploadedFileWithType | UploadedFileWithType[];
  // Momenta文档
  momenta_release_note?: UploadedFile | UploadedFile[] | UploadedFileWithType | UploadedFileWithType[];
  momenta_test_report?: UploadedFile | UploadedFile[] | UploadedFileWithType | UploadedFileWithType[];
}

// 多选顺序不影响“是否有变更”的判断
const vehicleListKey = (values: string[] | undefined) => JSON.stringify([...(values ?? [])].sort());

export const PackageForm: React.FC<PackageFormProps> = ({
  initialData,
  projects,
  projectId,
  fileTypes,
  onSubmit,
  onCancel,
  loading = false,
  isEdit = false,
  previousGitSources,
}) => {
  const { t, lang } = useT();
  const [form] = Form.useForm();
  const { setProgress, incrementManualRetry, incrementAutoRetry } = useUploadProgressStore();
  const [uploadedFiles, setUploadedFiles] = useState<FormFiles>({});
  const [submitting, setSubmitting] = useState(false);
  const [, forceUpdate] = useState(0);
  // 供应商文档模式
  const [releaseNoteMode, setReleaseNoteMode] = useState<'upload' | 'input'>('upload');
  const [testReportMode, setTestReportMode] = useState<'upload' | 'input'>('upload');
  const [firmwareModes, setFirmwareModes] = useState<Record<string, 'upload' | 'select' | 'git' | 'artifact'>>({});
  // 编辑模式初始快照，用于判断是否有变更
  const initialSnapshot = React.useRef<{
    uploadedFiles: FormFiles;
    firmwareModes: Record<string, string>;
    gitInfo: Record<string, { url: string; branch: string; commit: string; tag: string }>;
    artifactInfo: Record<string, string[]>;
    remarkInfo: Record<string, string>;
    releaseNoteMode: 'upload' | 'input';
    testReportMode: 'upload' | 'input';
    releaseNoteContent: string;
    testReportContent: string;
    externalReleaseIds: number[];
    name: string;
    vehicleModels: string;
    vehicleSubtypes: string;
    platforms: string;
    description: string;
  } | null>(null);
  // 编辑前已存在的 artifact 行，提交时对比出被删除/改名的旧行，显式传给后端删除
  const initialArtifactFiles = React.useRef<Record<string, { id: number; name: string }[]>>({});
  const [projectFilesCache, setProjectFilesCache] = useState<Record<string, ProjectFile[]>>({});
  const [loadingFiles, setLoadingFiles] = useState<Record<string, boolean>>({});
  const [editingFileKey, setEditingFileKey] = useState<string | null>(null);
  const [selectedNewFileTypeId, setSelectedNewFileTypeId] = useState<number | null>(null);
  const [updatingFileKey, setUpdatingFileKey] = useState<string | null>(null);
  const [gitInfo, setGitInfo] = useState<Record<string, { url: string; branch: string; commit: string; tag: string }>>({});
  const [artifactInfo, setArtifactInfo] = useState<Record<string, string[]>>({});
  const [remarkInfo, setRemarkInfo] = useState<Record<string, string>>({});
  const [deletedDocs, setDeletedDocs] = useState<Set<string>>(new Set());
  const [pendingDeleteFiles, setPendingDeleteFiles] = useState<Map<string, UploadedFileWithType[]>>(new Map());
  // 原用于等待跨 Tab 快照后再执行卡住检查，现在进度已存服务端，直接就绪
  const snapshotReady = true;

  // 获取当前项目配置
  const currentProject = projects.find(p => p.id === projectId);

  const vehicleModelsWatch = (Form.useWatch('vehicle_models', form) as string[] | undefined) ?? [];
  // 仅用于触子车型选项剪枝，避免数组引用变化导致 effect 反复执行
  const vehicleModelsKey = vehicleModelsWatch.join('\u0000');
  const savedModels = initialData?.vehicle_models?.length
    ? initialData.vehicle_models
    : initialData?.vehicle_model ? [initialData.vehicle_model] : [];
  const savedSubtypes = initialData?.vehicle_subtypes?.length
    ? initialData.vehicle_subtypes
    : initialData?.vehicle_subtype ? [initialData.vehicle_subtype] : [];
  const savedPlatforms = initialData?.platforms?.length
    ? initialData.platforms
    : initialData?.platform ? [initialData.platform] : [];

  // 车型/子车型候选来自当前项目已配置的组合（GET /projects/ 已带回 vehicle_types）。
  // 编辑历史数据时存量值可能不在配置里，一并补进来，保证标签可读、保存不丢值。
  const vehicleTypeOptions = useMemo(() => {
    const names = new Set<string>();
    (currentProject?.vehicle_types ?? []).forEach((vt) => {
      if (vt.vehicle_type) names.add(vt.vehicle_type);
    });
    savedModels.forEach((v) => names.add(v));
    return [...names].map((v) => ({ value: v, label: v }));
  }, [currentProject, savedModels.join('\u0000')]);

  // 项目配置里 子车型 -> 所属车型 的映射，用于多选联动剪枝
  const subtypeToModels = useMemo(() => {
    const map = new Map<string, Set<string>>();
    (currentProject?.vehicle_types ?? []).forEach((vt) => {
      if (!vt.vehicle_subtype) return;
      if (!map.has(vt.vehicle_subtype)) map.set(vt.vehicle_subtype, new Set());
      map.get(vt.vehicle_subtype)!.add(vt.vehicle_type);
    });
    return map;
  }, [currentProject]);

  const vehicleSubtypeOptions = useMemo(() => {
    const names = new Set<string>();
    const selectedModels = new Set(vehicleModelsWatch);
    (currentProject?.vehicle_types ?? [])
      .filter((vt) => selectedModels.has(vt.vehicle_type))
      .forEach((vt) => {
        if (vt.vehicle_subtype) names.add(vt.vehicle_subtype);
      });
    savedSubtypes.forEach((v) => {
      const owners = subtypeToModels.get(v);
      if (!owners || [...owners].some((m) => selectedModels.has(m))) names.add(v);
    });
    return [...names].map((v) => ({ value: v, label: v }));
  }, [currentProject, vehicleModelsKey, savedSubtypes.join('\u0000'), subtypeToModels]);

  const platformOptions = useMemo(() => {
    const names = new Set<string>();
    if (currentProject?.platform) names.add(currentProject.platform);
    savedPlatforms.forEach((v) => names.add(v));
    return [...names].map((v) => ({ value: v, label: v }));
  }, [currentProject?.platform, savedPlatforms.join('\u0000')]);

  // 车型变化后剪掉已失效的子车型；配置里查不到的存量值保留，避免编辑历史包丢数据。
  const prevModelsRef = useRef<string[]>([]);
  useEffect(() => {
    const prev = prevModelsRef.current;
    prevModelsRef.current = vehicleModelsWatch;
    // 首次填充/回显不算“移除车型”，避免把历史子车型误剪掉
    if (prev.length === 0) return;
    if (prev.length === vehicleModelsWatch.length && prev.every((m, i) => m === vehicleModelsWatch[i])) return;

    const selected = (form.getFieldValue('vehicle_subtypes') as string[] | undefined) ?? [];
    if (!selected.length) return;
    const next = selected.filter((sub) => {
      const owners = subtypeToModels.get(sub);
      if (!owners) return vehicleModelsWatch.length > 0;
      return [...owners].some((m) => vehicleModelsWatch.includes(m));
    });
    if (next.length !== selected.length) form.setFieldValue('vehicle_subtypes', next);
  }, [vehicleModelsKey, vehicleModelsWatch, subtypeToModels, form]);

  // 新建时若项目只有一个候选值则默认填充；只执行一次，用户清除后不再回填。
  const defaultsAppliedForProject = useRef<number | null>(null);
  useEffect(() => {
    if (isEdit || initialData || !projectId || !currentProject) return;
    if (defaultsAppliedForProject.current === projectId) return;
    defaultsAppliedForProject.current = projectId;

    const models = [...new Set(
      (currentProject.vehicle_types ?? []).map((vt) => vt.vehicle_type).filter(Boolean)
    )];
    const model = models.length === 1 ? models[0] : '';
    const subtypes = model
      ? [...new Set(
          (currentProject.vehicle_types ?? [])
            .filter((vt) => vt.vehicle_type === model)
            .map((vt) => vt.vehicle_subtype)
            .filter(Boolean)
        )]
      : [];

    const defaults: Record<string, string[]> = {};
    if (model && !form.isFieldTouched('vehicle_models')) defaults.vehicle_models = [model];
    if (subtypes.length === 1 && !form.isFieldTouched('vehicle_subtypes')) defaults.vehicle_subtypes = [subtypes[0]];
    if (currentProject.platform && !form.isFieldTouched('platforms')) defaults.platforms = [currentProject.platform];
    if (Object.keys(defaults).length > 0) form.setFieldsValue(defaults);
  }, [isEdit, initialData, projectId, currentProject, form]);

  // 获取底软供应商文件类型（以项目专属顺序为准，直接用 allowed_file_types）
  const supplierFileTypes = (currentProject?.allowed_file_types || []).filter(
    ft => ft.is_active && !('is_deleted' in ft && ft.is_deleted)
  );

  // 辅助函数：判断文件类型是否支持多文件上传
  const isMultiFileType = useCallback((fileType: FileType) => {
    return fileType.allow_multiple_files === true;
  }, []);

  // Artifact 多行输入：多文件类型可增删行，单文件类型固定一行
  const setArtifactRow = useCallback((code: string, index: number, value: string) => {
    setArtifactInfo((prev) => {
      const rows = [...(prev[code] || [''])];
      rows[index] = value;
      return { ...prev, [code]: rows };
    });
  }, []);

  const addArtifactRow = useCallback((code: string) => {
    setArtifactInfo((prev) => ({ ...prev, [code]: [...(prev[code] || ['']), ''] }));
  }, []);

  const removeArtifactRow = useCallback((code: string, index: number) => {
    setArtifactInfo((prev) => {
      const rows = [...(prev[code] || [''])];
      rows.splice(index, 1);
      return { ...prev, [code]: rows.length > 0 ? rows : [''] };
    });
  }, []);

  // 编辑界面对失败文件的重试：立即后台上传，与详情页 handleRetry 行为一致
  const handleFormRetry = async (file: UploadedFileWithType, rawFile: File) => {
    if (!file.uploadKey || !file.fileId || !initialData?.id) return;
    const packageId = initialData.id;
    const fileId = file.fileId;
    const key = file.uploadKey;

    incrementManualRetry(key);
    setProgress(key, { percent: 0, status: 'uploading', rawFile, uploadStartedAt: new Date().toISOString(), uploadFinishedAt: null, avgSpeedMbps: null });
    await updatePackageUploadStatus(packageId, 'uploading').catch(() => {});
    await startPackageFileUpload(packageId, fileId, 'uploading').catch(() => {});

    const startTime = Date.now();
    // 心跳：刷新 updated_at，防止其他 Tab 把正在进行的重传误判为卡住
    let currentPercent = 0;
    const heartbeatTimer = setInterval(() => {
      heartbeatPackageFile(packageId, fileId, currentPercent).catch(() => {});
    }, 1 * 60 * 1000);
    try {
      const result = await uploadFile(rawFile, (p) => {
        setProgress(key, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile });
        currentPercent = p.percent;
      }, undefined, () => { incrementAutoRetry(key); }, key);
      const isUploadType = !file.sourceType || file.sourceType === 'upload';
      if (isUploadType && !result.md5) {
        setProgress(key, { percent: 0, status: 'failed', rawFile, uploadFinishedAt: new Date().toISOString() });
        await failPackageFileUpload(packageId, fileId).catch(() => {});
        return;
      }
      const finishedAt = new Date().toISOString();
      const durationSec = (Date.now() - startTime) / 1000;
      const avgSpeedMbps = durationSec > 0 ? parseFloat((result.fileSize / 1024 / 1024 / durationSec).toFixed(2)) : null;
      const startedAt = useUploadProgressStore.getState().getProgress(key)?.uploadStartedAt ?? null;
      await completePackageFileUpload(packageId, fileId, {
        file_url: result.fileUrl,
        object_key: result.objectKey,
        file_size: result.fileSize,
        checksum: result.md5 || '',
        file_name: rawFile.name,
        upload_started_at: startedAt,
        upload_finished_at: finishedAt,
        avg_speed_mbps: avgSpeedMbps,
      });
      setProgress(key, { percent: 100, status: 'done', uploadFinishedAt: finishedAt, avgSpeedMbps });
      setUploadedFiles(prev => {
        const stateKey = Object.keys(prev).find(k => {
          const entry = prev[k];
          if (Array.isArray(entry)) {
            return entry.some(f => (f as UploadedFileWithType).uploadKey === key);
          }
          return (entry as UploadedFileWithType)?.uploadKey === key;
        });
        if (!stateKey) return prev;
        const entry = prev[stateKey];
        const patch = { md5: result.md5 || '', fileUrl: result.fileUrl, objectKey: result.objectKey, fileSize: result.fileSize, fileName: rawFile.name };
        if (Array.isArray(entry)) {
          return { ...prev, [stateKey]: entry.map(f => (f as UploadedFileWithType).uploadKey === key ? { ...f, ...patch } : f) };
        }
        return { ...prev, [stateKey]: { ...entry, ...patch } };
      });
    } catch {
      // 若 progress store 已被删除操作清除，说明是删除导致的中止，跳过 fail 标记
      if (!useUploadProgressStore.getState().progress[key]) return;
      setProgress(key, { percent: 0, status: 'failed', rawFile, uploadFinishedAt: new Date().toISOString() });
      await failPackageFileUpload(packageId, fileId).catch(() => {});
    } finally {
      clearInterval(heartbeatTimer);
    }
  };

  const handleFormDocRetry = async (
    docType: 'release_note' | 'test_report',
    rawFile: File,
    file?: UploadedFileWithType,
  ) => {
    if (!initialData?.id) return;
    const packageId = initialData.id;
    const key = file?.uploadKey || `package-${packageId}-doc-${docType}`;
    incrementManualRetry(key);
    setProgress(key, { percent: 0, status: 'uploading', rawFile, uploadStartedAt: new Date().toISOString(), uploadFinishedAt: null, avgSpeedMbps: null });
    await updatePackageUploadStatus(packageId, 'uploading').catch(() => {});
    const startTime = Date.now();
    // 心跳：刷新 package.updated_at，防止其他 Tab 把正在进行的文档重传误判为卡住
    const heartbeatTimer = setInterval(() => {
      patchPackage(packageId, {}).catch(() => {});
    }, 1 * 60 * 1000);
    try {
      const result = await uploadFile(rawFile, (p) => {
        setProgress(key, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile });
      }, undefined, () => { incrementAutoRetry(key); }, key);
      const finishedAt = new Date().toISOString();
      const durationSec = (Date.now() - startTime) / 1000;
      const avgSpeedMbps = durationSec > 0 ? parseFloat((result.fileSize / 1024 / 1024 / durationSec).toFixed(2)) : null;
      if (docType === 'test_report') {
        const current = Array.isArray(uploadedFiles.test_report)
          ? [...uploadedFiles.test_report as UploadedFileWithType[]]
          : [];
        const index = file
          ? current.findIndex((f) => f === file || (!!f.uploadKey && f.uploadKey === file.uploadKey))
          : -1;
        const updated: UploadedFileWithType = {
          ...(index >= 0 ? current[index] : {
            objectKey: '',
            fileUrl: '',
            fileName: rawFile.name,
            fileSize: 0,
            fileTypeId: 0,
            fileId: -1,
            sourceType: 'upload' as const,
          }),
          fileUrl: result.fileUrl,
          objectKey: result.objectKey,
          fileName: rawFile.name,
          fileSize: result.fileSize,
          md5: result.md5 || '',
          rawFile: undefined,
          uploadKey: key,
        };
        if (index >= 0) current[index] = updated;
        else current.push(updated);
        await patchPackage(packageId, {
          test_report_files: current.map((f) => ({
            url: f.fileUrl || '', name: f.fileName || '',
            size: f.fileSize || 0, checksum: f.md5 || '',
          })),
          doc_files_merge: true,
        });
        setUploadedFiles(prev => ({ ...prev, test_report: current }));
      } else {
        await patchPackage(packageId, {
          [`${docType}_url`]: result.fileUrl,
          [`${docType}_name`]: rawFile.name,
          [`${docType}_size`]: result.fileSize,
          [`${docType}_checksum`]: result.md5 || '',
        });
        setUploadedFiles(prev => ({
          ...prev,
          [docType]: {
            ...(prev[docType] as UploadedFile),
            md5: result.md5 || '',
            fileUrl: result.fileUrl,
            objectKey: result.objectKey,
            fileSize: result.fileSize,
            fileName: rawFile.name,
          },
        }));
      }
      setProgress(key, { percent: 100, status: 'done', uploadFinishedAt: finishedAt, avgSpeedMbps });
    } catch {
      // 若 progress store 已被删除操作清除，说明是删除导致的中止，跳过 fail 标记
      if (!useUploadProgressStore.getState().progress[key]) return;
      setProgress(key, { percent: 0, status: 'failed', rawFile, uploadFinishedAt: new Date().toISOString() });
    } finally {
      clearInterval(heartbeatTimer);
    }
  };

  const fetchFilesForType = useCallback(async (fileTypeCode: string, fileTypeId: number) => {
    if (loadingFiles[fileTypeCode] || !projectId) return;
    setLoadingFiles((prev) => ({ ...prev, [fileTypeCode]: true }));
    try {
      const files = await getProjectFiles(
        projectId,
        fileTypeId,
        initialData?.id
      );
      setProjectFilesCache((prev) => ({ ...prev, [fileTypeCode]: files }));
    } catch {
      message.error(t('获取已有文件列表失败'));
    } finally {
      setLoadingFiles((prev) => ({ ...prev, [fileTypeCode]: false }));
    }
  }, [projectId, initialData?.id, t]);

  const [availableExternalReleases, setAvailableExternalReleases] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    if (!projectId) return;
    listExternalReleases({ project_id: projectId, page_size: 200 })
      .then((res) => setAvailableExternalReleases(res.results.map((r) => ({ id: r.id, name: r.name }))))
      .catch(() => {});
  }, [projectId]);

  useEffect(() => {
    if (!initialData) return;
    if (!snapshotReady) return; // 等待跨 Tab 快照，避免误判 isStuckUploading
      const formValues: Record<string, unknown> = {
        name: initialData.name,
        vehicle_models: initialData.vehicle_models?.length
          ? initialData.vehicle_models
          : initialData.vehicle_model ? [initialData.vehicle_model] : [],
        vehicle_subtypes: initialData.vehicle_subtypes?.length
          ? initialData.vehicle_subtypes
          : initialData.vehicle_subtype ? [initialData.vehicle_subtype] : [],
        platforms: initialData.platforms?.length
          ? initialData.platforms
          : initialData.platform ? [initialData.platform] : [],
        description: initialData.description,
        external_release_ids: initialData.external_releases?.map((r) => r.id) ?? [],
      };

      // Map existing files to uploadedFiles state
      const existingFiles: FormFiles = {};
      const modesState: Record<string, 'upload' | 'select' | 'git' | 'artifact'> = {};
      const gitInfoState: Record<string, { url: string; branch: string; commit: string; tag: string }> = {};
      const artifactInfoState: Record<string, string[]> = {};
      initialArtifactFiles.current = {};
      const remarkInfoState: Record<string, string> = {};

      // Add firmware files (with fileId for deletion)
      initialData.files?.forEach((file) => {
        // Only process supplier files; momenta files are managed in ExternalRelease
        if (file.file_source === 'momenta') return;
        if (file.file_type?.code) {
          const sourceType = file.source_type || 'upload';
          const stateKey = file.file_type.code;

          if (sourceType === 'git') {
            // Git source: only restore git info, don't add to uploadedFiles
            modesState[stateKey] = 'git';
            gitInfoState[stateKey] = {
              url: file.git_url || '',
              branch: file.git_branch || '',
              commit: file.git_commit || '',
              tag: file.git_tag || '',
            };
            remarkInfoState[stateKey] = file.remark || '';
            // Don't add to existingFiles - let git inputs show git info when in git mode
          } else if (sourceType === 'artifact') {
            // Artifact source: restore artifact name(s); 单文件类型只保留第一条
            modesState[stateKey] = 'artifact';
            (initialArtifactFiles.current[stateKey] ||= []).push({ id: file.id, name: file.artifact_name || '' });
            const names = artifactInfoState[stateKey] || (artifactInfoState[stateKey] = []);
            const name = file.artifact_name || '';
            if (isMultiFileType(file.file_type)) {
              if (name && !names.includes(name)) names.push(name);
            } else if (names.length === 0) {
              names.push(name);
            }
            remarkInfoState[stateKey] = file.remark || '';
          } else {
            // Upload or select source: add to uploadedFiles
            modesState[stateKey] = sourceType === 'select' ? 'select' : 'upload';
            const uploadKey = `package-${initialData.id}-file-${file.id}`;
            const storeEntry = useUploadProgressStore.getState().progress[uploadKey];
            const fileUpdatedAt = file.updated_at ? new Date(file.updated_at).getTime() : Date.now();
            const isStuckUploading = (file.upload_status === 'uploading' || file.upload_status === 'queued')
              && !storeEntry
              && (Date.now() - fileUpdatedAt) > 10 * 60 * 1000;
            const uploadedFile: UploadedFileWithType = {
              objectKey: file.object_key || '',
              fileUrl: file.file_url,
              fileName: file.file_name,
              fileSize: file.file_size,
              fileTypeId: file.file_type.id,
              fileId: file.id, // Store file ID for deletion
              md5: file.checksum || '',  // Include MD5 checksum
              remark: file.remark || '',
              sourceType: sourceType as 'upload' | 'select',
              file_source: (file as any).file_source,  // supplier or momenta
              uploadKey,
              uploadStatus: isStuckUploading ? 'failed' : ((file.upload_status as UploadedFileWithType['uploadStatus']) || 'done'),
            };
            if (isStuckUploading && initialData.id && file.id) {
              failPackageFileUpload(initialData.id, file.id).catch(() => {});
            }

            // 关键改动：根据配置决定使用数组还是单对象
            const fileTypeConfig = fileTypes.find((ft) => ft.code === file.file_type.code);
            if (fileTypeConfig && isMultiFileType(fileTypeConfig)) {
              // 多文件类型：追加到数组
              if (!existingFiles[stateKey]) {
                existingFiles[stateKey] = [];
              }
              (existingFiles[stateKey] as UploadedFileWithType[]).push(uploadedFile);
            } else {
              // 单文件类型：直接赋值（后者覆盖前者）
              existingFiles[stateKey] = uploadedFile;
            }
          }
        }
      });

      // Set firmware modes and git/artifact info
      setFirmwareModes(modesState);
      setGitInfo(gitInfoState);
      setArtifactInfo(artifactInfoState);
      setRemarkInfo(remarkInfoState);

      // Add release note: store 活跃进度优先，再看 initialData URL
      const releaseNoteType = fileTypes.find(ft => ft.code === 'release_note');
      if (initialData.id) {
        const rnKey = `package-${initialData.id}-doc-release_note`;
        const storeEntry = useUploadProgressStore.getState().progress[rnKey];
        console.log('[PackageForm] init release_note storeEntry:', storeEntry, 'key:', rnKey);
        if (storeEntry && storeEntry.status !== 'done') {
          existingFiles.release_note = {
            objectKey: '',
            fileUrl: '',
            fileName: storeEntry.rawFile?.name || 'Release Note',
            fileSize: storeEntry.rawFile?.size || 0,
            fileTypeId: releaseNoteType?.id || 0,
            fileId: -1,
            sourceType: 'upload' as const,
            rawFile: storeEntry.rawFile as RcFile,
            uploadKey: rnKey,
            uploadStatus: storeEntry.status === 'failed' ? 'failed' : undefined,
          };
        }
      }
      // store 无记录 + name 有值但 url 为空 = 刷新导致上传中断，显示为失败状态供用户重试
      if (!existingFiles.release_note && initialData.release_note_name && !initialData.release_note_url) {
        existingFiles.release_note = {
          objectKey: '',
          fileUrl: '',
          fileName: initialData.release_note_name,
          fileSize: initialData.release_note_size || 0,
          fileTypeId: releaseNoteType?.id || 0,
          fileId: -1,
          sourceType: 'upload' as const,
          uploadStatus: 'failed',
        };
        if (initialData.release_note_content) {
          formValues.release_note_content = initialData.release_note_content;
        }
      }
      if (!existingFiles.release_note && initialData.release_note_url) {
        existingFiles.release_note = {
          objectKey: '',
          fileUrl: initialData.release_note_url,
          fileName: initialData.release_note_name || '',
          fileSize: initialData.release_note_size || 0,
          md5: initialData.release_note_checksum || '',
          fileTypeId: releaseNoteType?.id || 0,
          fileId: -1,
          sourceType: 'upload' as const,
        };
        setReleaseNoteMode('upload');
        if (initialData.release_note_content) {
          formValues.release_note_content = initialData.release_note_content;
        }
      } else if (!existingFiles.release_note && initialData.release_note_content) {
        formValues.release_note_content = initialData.release_note_content;
        setReleaseNoteMode('input');
      }

      // Add test reports (multi-file list; legacy single scalar becomes one entry)
      const testReportType = fileTypes.find(ft => ft.code === 'test_report');
      const trEntries = initialData.test_report_files?.length
        ? initialData.test_report_files
        : (initialData.test_report_url || initialData.test_report_name)
          ? [{
              url: initialData.test_report_url || '',
              name: initialData.test_report_name || '',
              size: initialData.test_report_size || 0,
              checksum: initialData.test_report_checksum || '',
            }]
          : [];
      const trFiles: UploadedFileWithType[] = trEntries.map((entry) => ({
        objectKey: '',
        fileUrl: entry.url || '',
        fileName: entry.name || '',
        fileSize: entry.size || 0,
        md5: entry.checksum || '',
        fileTypeId: testReportType?.id || 0,
        fileId: -1,
        sourceType: 'upload' as const,
        uploadStatus: !entry.url && initialData.test_report_upload_status === 'failed'
          ? 'failed' as const
          : undefined,
      }));
      // 恢复未完成上传的本标签页进度（新后缀 key，兼容旧的单 key）
      if (initialData.id) {
        const progress = useUploadProgressStore.getState().progress;
        const prefix = `package-${initialData.id}-doc-test_report`;
        Object.entries(progress).forEach(([key, entry]) => {
          if (!entry || entry.status === 'done') return;
          if (key !== prefix && !key.startsWith(`${prefix}-`)) return;
          const rawName = entry.rawFile?.name || '测试报告';
          const restored: UploadedFileWithType = {
            objectKey: '',
            fileUrl: '',
            fileName: rawName,
            fileSize: entry.rawFile?.size || 0,
            fileTypeId: testReportType?.id || 0,
            fileId: -1,
            sourceType: 'upload' as const,
            rawFile: entry.rawFile as RcFile,
            uploadKey: key,
            uploadStatus: entry.status === 'failed' ? 'failed' : undefined,
          };
          const index = Number(key.slice(prefix.length + 1));
          const pending = key !== prefix && Number.isInteger(index) ? trFiles[index] : undefined;
          if (pending && !pending.fileUrl) {
            trFiles[index] = { ...pending, ...restored };
          } else {
            trFiles.push(restored);
          }
        });
      }
      existingFiles.test_report = trFiles.length > 0 ? trFiles : undefined;
      if (trFiles.some((f) => f.fileUrl)) {
        setTestReportMode('upload');
        if (initialData.test_report_content) {
          formValues.test_report_content = initialData.test_report_content;
        }
      } else if (trFiles.length === 0 && initialData.test_report_content) {
        formValues.test_report_content = initialData.test_report_content;
        setTestReportMode('input');
      }

      // Add Momenta release note (check both URL and content)
      if (initialData.momenta_release_note_url) {
        const momentaReleaseNote: UploadedFileWithType = {
          objectKey: '',
          fileUrl: initialData.momenta_release_note_url,
          fileName: initialData.momenta_release_note_name || '',
          fileSize: initialData.momenta_release_note_size || 0,
          md5: initialData.momenta_release_note_checksum || '',
          fileTypeId: releaseNoteType?.id || 0,
          fileId: -1,
          sourceType: 'upload' as const,
        };
        existingFiles.momenta_release_note = momentaReleaseNote;
      }

      // Add Momenta test report (check both URL and content)
      if (initialData.momenta_test_report_url) {
        const momentaTestReport: UploadedFileWithType = {
          objectKey: '',
          fileUrl: initialData.momenta_test_report_url,
          fileName: initialData.momenta_test_report_name || '',
          fileSize: initialData.momenta_test_report_size || 0,
          md5: initialData.momenta_test_report_checksum || '',
          fileTypeId: testReportType?.id || 0,
          fileId: -1,
          sourceType: 'upload' as const,
        };
        existingFiles.momenta_test_report = momentaTestReport;
      }

      setUploadedFiles(existingFiles);
      setDeletedDocs(new Set());

      // Restore upload configuration from previous upload
      form.setFieldsValue(formValues);

      // 记录初始快照，用于判断编辑时是否有变更（只记录一次）
      const initRnMode: 'upload' | 'input' = (!existingFiles.release_note && initialData.release_note_content && !initialData.release_note_url) ? 'input' : 'upload';
      const initTrMode: 'upload' | 'input' = (!existingFiles.test_report && initialData.test_report_content
        && !initialData.test_report_url && !initialData.test_report_files?.length) ? 'input' : 'upload';
      initialSnapshot.current = {
        uploadedFiles: { ...existingFiles },
        firmwareModes: { ...modesState },
        gitInfo: { ...gitInfoState },
        artifactInfo: Object.fromEntries(Object.entries(artifactInfoState).map(([k, v]) => [k, [...v]])),
        remarkInfo: { ...remarkInfoState },
        releaseNoteMode: initRnMode,
        testReportMode: initTrMode,
        releaseNoteContent: (initialData.release_note_content || '') as string,
        testReportContent: (initialData.test_report_content || '') as string,
        externalReleaseIds: initialData.external_releases?.map((r) => r.id) ?? [],
        name: (initialData.name || '') as string,
        vehicleModels: vehicleListKey(initialData.vehicle_models?.length ? initialData.vehicle_models : (initialData.vehicle_model ? [initialData.vehicle_model] : [])),
        vehicleSubtypes: vehicleListKey(initialData.vehicle_subtypes?.length ? initialData.vehicle_subtypes : (initialData.vehicle_subtype ? [initialData.vehicle_subtype] : [])),
        platforms: vehicleListKey(initialData.platforms?.length ? initialData.platforms : (initialData.platform ? [initialData.platform] : [])),
        description: (initialData.description || '') as string,
      };
  }, [initialData, form, fileTypes, isMultiFileType, snapshotReady]);

  const handleUpdateFileType = async () => {
    if (!editingFileKey || !selectedNewFileTypeId || !initialData?.id) {
      return;
    }

    const file = uploadedFiles[editingFileKey] as UploadedFileWithType | undefined;
    if (!file || !('fileId' in file) || !file.fileId) {
      message.error(t('无法更新新上传的文件类型，请删除后重新上传'));
      return;
    }

    setUpdatingFileKey(editingFileKey);

    try {
      const newFileType = fileTypes.find((ft) => ft.id === selectedNewFileTypeId);
      if (!newFileType) {
        message.error(t('无效的文件类型'));
        return;
      }

      await updatePackageFile(initialData.id, file.fileId, selectedNewFileTypeId, file.file_source || 'supplier');
      message.success(t('文件类型已更新'));

      // Mark this file as moved so it doesn't show under old type
      if (file.fileId) {
      }

      // Update uploadedFiles state: move file from old category to new category
      setUploadedFiles((prev) => {
        const updated = { ...prev };
        const updatedFile: UploadedFileWithType = {
          ...file,
          fileTypeId: selectedNewFileTypeId,
        };
        delete updated[editingFileKey];
        updated[newFileType.code] = updatedFile;
        return updated;
      });

      setEditingFileKey(null);
      setSelectedNewFileTypeId(null);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string; message?: string } } };
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || t('更新文件类型失败');
      message.error(errorMsg);
      console.error('更新文件类型失败:', error);
    } finally {
      setUpdatingFileKey(null);
    }
  };

  const handleClearGit = (fileTypeCode: string) => {
    setGitInfo((prev) => {
      const updated = { ...prev };
      delete updated[fileTypeCode];
      return updated;
    });
    setRemarkInfo((prev) => {
      const updated = { ...prev };
      delete updated[fileTypeCode];
      return updated;
    });
    setFirmwareModes((prev) => ({ ...prev, [fileTypeCode]: 'upload' }));
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    const retryCompleteUpload = async (fn: () => Promise<void>, maxRetries = 3, delayMs = 60_000) => {
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          await fn();
          return;
        } catch (err: unknown) {
          const isRetryable = (err as { response?: { status?: number } })?.response?.status === undefined
            || [502, 503, 504].includes((err as { response?: { status?: number } })?.response?.status ?? 0);
          if (attempt < maxRetries && isRetryable) {
            await new Promise(resolve => setTimeout(resolve, delayMs));
            continue;
          }
          throw err;
        }
      }
    };
    // Combine supplier file types only
    const allFirmwareItems = [
      ...supplierFileTypes.map(ft => ({ ft, stateKey: ft.code, source: 'supplier' as const })),
    ];

    // Collect firmware files that have either upload/select mode with file or git mode with git info
    const firmwareFiles: Array<{ stateKey: string; source: 'supplier' | 'momenta'; ft: typeof allFirmwareItems[0]['ft'] }> = [];

    for (const { ft, stateKey, source } of allFirmwareItems) {
      const mode = firmwareModes[stateKey] || 'upload';
      const uploadedFile = uploadedFiles[stateKey];

      if (mode === 'git') {
        // Git mode: validate git info is complete (commit and tag are mutually optional)
        const git = gitInfo[stateKey];
        const hasAnyGitField = git && (git.url || git.branch || git.commit || git.tag);
        const hasAllRequiredFields = git && git.url && git.branch && (git.commit || git.tag);

        // 如果部分填写（有任何字段但不完整），立即警告并返回
        if (hasAnyGitField && !hasAllRequiredFields) {
          message.warning({
            content: t('{code} 的Git信息填写不完整。请填写完整的Git URL、分支，以及Commit ID或Tag（至少选填其一）。', { code: ft.code }),
            duration: 5,
          });
          return;
        }

        // 完整填写时，添加到已有文件列表
        if (hasAllRequiredFields) {
          firmwareFiles.push({ stateKey, source, ft });
        }
        // 完全未填写不做处理（不添加到firmwareFiles）
      } else if (mode === 'artifact') {
        // Artifact mode: 多文件类型可填多个产物包名
        const names = (artifactInfo[stateKey] || []).map((n) => n.trim()).filter(Boolean);
        if (names.length > 0) {
          firmwareFiles.push({ stateKey, source, ft });
        }
        // 未填写不添加
      } else {
        // Upload or select mode: require uploaded file
        // 多文件支持：检查uploadedData（可能是数组或单对象）
        const hasFiles = uploadedFile
          ? (Array.isArray(uploadedFile) ? uploadedFile.length > 0 : true)
          : false;

        if (hasFiles) {
          firmwareFiles.push({ stateKey, source, ft });
        }
      }
    }

    const supplierFirmwareCount = firmwareFiles.filter(item => item.source === 'supplier').length;

    if (supplierFirmwareCount === 0) {
      message.error(t('请至少上传一个固件文件或提供Git配置'));
      return;
    }

    // 仅当有供应商固定文件时，才要求供应商文档
    if (supplierFirmwareCount > 0) {
      if (isEdit) {
        // 编辑模式：考虑待删除标记，检查有效状态（文件或文字至少保留其一）
        // 区分新旧文件：待删除文件有 fileId，新上传文件 fileId 为 undefined
        const rnPendingDeleteFiles = pendingDeleteFiles.get('release_note') || [];
        const trPendingDeleteFiles = pendingDeleteFiles.get('test_report') || [];
        const rnText = form.getFieldValue('release_note_content') as string;
        const trText = form.getFieldValue('test_report_content') as string;

        const rnCurrentFile = uploadedFiles.release_note as UploadedFileWithType | undefined;
        const effectiveRnFile = !!(rnCurrentFile && (
          rnPendingDeleteFiles.length === 0 ||
          !rnPendingDeleteFiles.some(f => f.fileId && f.fileId === rnCurrentFile.fileId)
        ));
        if (!effectiveRnFile && !rnText) {
          message.error(t('Release Note 不能为空，请保留文件或输入内容'));
          return;
        }
        const trFiles = Array.isArray(uploadedFiles.test_report)
          ? uploadedFiles.test_report as UploadedFileWithType[]
          : uploadedFiles.test_report
            ? [uploadedFiles.test_report as UploadedFileWithType]
            : [];
        const trPendingKeys = new Set(trPendingDeleteFiles.map(f => f.fileUrl || f.fileName));
        const effectiveTrFiles = trFiles.filter(f => !trPendingKeys.has(f.fileUrl || f.fileName));
        if (effectiveTrFiles.length === 0 && !trText) {
          message.error(t('测试报告不能为空，请保留文件或输入内容'));
          return;
        }
      } else {
        // 创建模式：基于当前选择的模式验证
        if (releaseNoteMode === 'upload' && !uploadedFiles.release_note) {
          message.error(t('请上传 Release Note 文件'));
          return;
        }
        if (releaseNoteMode === 'input' && !values.release_note_content) {
          message.error(t('请输入 Release Note 内容'));
          return;
        }
        const trFiles = Array.isArray(uploadedFiles.test_report)
          ? uploadedFiles.test_report
          : uploadedFiles.test_report ? [uploadedFiles.test_report] : [];
        if (testReportMode === 'upload' && trFiles.length === 0) {
          message.error(t('请上传测试报告文件'));
          return;
        }
        if (testReportMode === 'input' && !values.test_report_content) {
          message.error(t('请输入测试报告内容'));
          return;
        }
      }
    }

    // 校验 multi-file 类型中是否有同名文件
    for (const ft of supplierFileTypes) {
      if (!isMultiFileType(ft)) continue;
      const files = uploadedFiles[ft.code];
      if (!Array.isArray(files) || files.length === 0) continue;
      const names = (files as UploadedFileWithType[]).map((f) => f.fileName);
      const duplicates = names.filter((n: string, i: number) => names.indexOf(n) !== i);
      if (duplicates.length > 0) {
        message.error(t('{name} 中存在同名文件：{names}，请删除重复文件后再提交', { name: ft.name, names: [...new Set(duplicates)].join(', ') }));
        return;
      }
    }
    // Artifact 同一文件类型内不允许重复产物包名
    for (const ft of supplierFileTypes) {
      if ((firmwareModes[ft.code] || 'upload') !== 'artifact') continue;
      const names = (artifactInfo[ft.code] || []).map((n) => n.trim()).filter(Boolean);
      const dup = names.filter((n, i) => names.indexOf(n) !== i);
      if (dup.length > 0) {
        message.error(t('{name} 中存在重复的 Artifact 产物包名：{names}，请修改后再提交', { name: ft.name, names: [...new Set(dup)].join(', ') }));
        return;
      }
    }
    // 测试报告多附件同样不允许同名（后端按 name 合并延迟回写）
    const trDupCheck = Array.isArray(uploadedFiles.test_report)
      ? uploadedFiles.test_report as UploadedFileWithType[]
      : [];
    const trDupNames = trDupCheck.map((f) => f.fileName).filter(Boolean);
    const trDup = trDupNames.filter((n, i) => trDupNames.indexOf(n) !== i);
    if (trDup.length > 0) {
      message.error(t('测试报告中存在同名文件：{names}，请删除重复文件后再提交', { names: [...new Set(trDup)].join(', ') }));
      return;
    }

    setSubmitting(true);

    try {
      // Snapshot pending deletes BEFORE building the payload.
      // Instead of calling DELETE API separately (which creates an extra log entry),
      // we pass the IDs to the PUT request so the backend deletes + logs in one operation.
      const capturedPendingDeletes = new Map(pendingDeleteFiles);
      console.log('[PackageForm:submit] capturedPendingDeletes=', [...capturedPendingDeletes.entries()].map(([k, v]) => ({ typeCode: k, files: v.map(f => ({ fileId: f.fileId, fileName: f.fileName })) })));
      setPendingDeleteFiles(new Map());

      // Effective deleted docs: current state + newly pending deletions (setDeletedDocs is async)
      const effectiveDeletedDocs = new Set([
        ...deletedDocs,
        ...(capturedPendingDeletes.has('release_note') ? ['release_note'] : []),
        ...(capturedPendingDeletes.has('test_report') ? ['test_report'] : []),
      ]);

      const files: PackageFormData['files'] = [];

      // Build files array from collected firmware files
      for (const { stateKey, source, ft: fileType } of firmwareFiles) {
        const mode = firmwareModes[stateKey] || 'upload';
        const file_source = source;

        if (mode === 'git') {
          // Git source: use git info from gitInfo state (commit and tag are mutually optional)
          const git = gitInfo[stateKey];
          if (!git || !git.url || !git.branch || (!git.commit && !git.tag)) {
            throw new Error(t('Git信息不完整 (需要Commit ID或Tag): {code}', { code: fileType.code }));
          }

          const fileData: PackageFormData['files'][0] = {
            file_type_id: fileType.id,
            file_name: `Git-${git.url.split('/').pop()}-${git.branch}-${git.commit ? git.commit.substring(0, 6) : git.tag}`,
            file_size: 0, // Size will be determined from git
            source_type: 'git',
            git_url: git.url,
            git_branch: git.branch,
            git_commit: git.commit || '',
            git_tag: git.tag || '',
            file_source,
            remark: remarkInfo[stateKey] ?? '',
          };
          files.push(fileData);
        } else if (mode === 'artifact') {
          // Artifact source: 多文件类型每个产物包名一条记录
          const names = (artifactInfo[stateKey] || []).map((n) => n.trim()).filter(Boolean);
          if (names.length === 0) {
            throw new Error(t('Artifact名称不能为空: {code}', { code: fileType.code }));
          }
          for (const name of names) {
            const fileData: PackageFormData['files'][0] = {
              file_type_id: fileType.id,
              file_name: name,
              file_size: 0,
              source_type: 'artifact',
              artifact_name: name,
              file_source,
              remark: remarkInfo[stateKey] ?? '',
            };
            files.push(fileData);
          }
        } else {
          // Upload or select source: include file_url
          const uploadedData = uploadedFiles[stateKey];
          if (!uploadedData) {
            throw new Error(t('文件不存在: {code}', { code: fileType.code }));
          }

          // Build array and filter out files that were marked for deferred deletion.
          // uploadedFiles still reflects old state (React async), so we use capturedPendingDeletes.
          const deletedFileIds = new Set(
            (capturedPendingDeletes.get(stateKey) || [])
              .map(f => f.fileId).filter((id): id is number => id != null)
          );
          const rawArray = isMultiFileType(fileType)
            ? (uploadedData as UploadedFileWithType[])
            : [uploadedData as UploadedFileWithType];
          const filesArray = deletedFileIds.size > 0
            ? rawArray.filter(f => !f.fileId || !deletedFileIds.has(f.fileId))
            : rawArray;

          if (filesArray.length === 0) {
            // All files for this type were deleted without replacement — skip this type
            console.log('[PackageForm:submit] all files for type', stateKey, 'were deleted, skipping from payload');
            continue;
          }

          // 遍历该类型的所有文件（单文件时数组长度为1）
          for (const fileWithType of filesArray) {
            // Use the sourceType from the file itself (upload/select), not from the mode
            const sourceType: 'select' | 'upload' = fileWithType.sourceType === 'select' ? 'select' : 'upload';
            // Deferred file: rawFile present means not yet uploaded
            const isDeferred = !!(fileWithType as UploadedFileWithType).rawFile;
            const fileData: PackageFormData['files'][0] = {
              file_type_id: fileType.id,
              file_name: fileWithType.fileName,
              file_url: isDeferred ? '' : fileWithType.fileUrl,
              file_size: isDeferred ? fileWithType.fileSize : fileWithType.fileSize,
              object_key: isDeferred ? '' : (fileWithType.objectKey || ''),
              checksum: isDeferred ? '' : (fileWithType.md5 || ''),
              source_type: sourceType,
              file_source,
              upload_status: isDeferred ? 'queued' : 'done',
              remark: fileWithType.remark ?? '',
              rawFile: isDeferred ? (fileWithType as UploadedFileWithType).rawFile : undefined,
            };
            files.push(fileData);
          }
        }
      }

      console.log('[PackageForm:submit] files to submit:', files.map(f => ({ file_type_id: f.file_type_id, file_name: f.file_name, file_url: f.file_url, source_type: f.source_type, file_source: f.file_source })));
      const vehicleModels = (values.vehicle_models as string[] | undefined) ?? [];
      const vehicleSubtypes = (values.vehicle_subtypes as string[] | undefined) ?? [];
      const platforms = (values.platforms as string[] | undefined) ?? [];
      const data: PackageFormData = {
        name: values.name as string,
        // 下游 BCR/BAT 仍读单值：始终取数组首值
        vehicle_model: vehicleModels[0] ?? '',
        vehicle_subtype: vehicleSubtypes[0] ?? '',
        platform: platforms[0] ?? '',
        vehicle_models: vehicleModels,
        vehicle_subtypes: vehicleSubtypes,
        platforms,
        description: values.description as string | undefined,
        files,
        external_release_ids: (values.external_release_ids as number[]) ?? [],
      };

      // Add release note data - IMPROVED: Priority-based instead of mode-based
      // 改进逻辑：优先使用有数据的内容，而不是完全依赖用户选择的模式
      const releaseNoteFile = uploadedFiles.release_note;
      const releaseNoteText = form.getFieldValue('release_note_content') as string;
      const releaseNoteDeferred = releaseNoteFile && !Array.isArray(releaseNoteFile)
        && !(releaseNoteFile as UploadedFileWithType).fileUrl
        && !!(releaseNoteFile as UploadedFileWithType).rawFile;

      if (releaseNoteDeferred) {
        // Deferred: upload after submit, just record the file name for now
        data.release_note_name = (releaseNoteFile as UploadedFileWithType).fileName || '';
        data.release_note_size = (releaseNoteFile as UploadedFileWithType).fileSize || 0;
      } else if (releaseNoteFile && !Array.isArray(releaseNoteFile) && releaseNoteFile.fileUrl) {
        data.release_note_url = releaseNoteFile.fileUrl;
        data.release_note_name = releaseNoteFile.fileName || '';
        data.release_note_size = releaseNoteFile.fileSize || 0;
        data.release_note_checksum = releaseNoteFile.md5 || '';
      } else if (releaseNoteText) {
        // Priority 2: If text was entered, use it
        // 优先级2：没有上传文件但有输入内容，使用内容

        // Preserve existing URL in edit mode
        if (isEdit && initialData?.release_note_url) {
          data.release_note_url = initialData.release_note_url;
          data.release_note_name = initialData.release_note_name;
          data.release_note_size = initialData.release_note_size;
          data.release_note_checksum = initialData.release_note_checksum;
        }
      } else if (isEdit && !effectiveDeletedDocs.has('release_note') && (initialData?.release_note_url || initialData?.release_note_content)) {
        // Priority 3: In edit mode without new data, keep original
        // 优先级3：编辑模式下没有新数据，保持原有（未删除时）
        if (initialData.release_note_url) {
          data.release_note_url = initialData.release_note_url;
          data.release_note_name = initialData.release_note_name;
          data.release_note_size = initialData.release_note_size;
          data.release_note_checksum = initialData.release_note_checksum;
        }
      }
      // 文字内容以表单当前值为准：用户清空就发空串，否则后端会保留旧值。
      // 只有表单从未初始化过该字段（undefined）时才回退到原值。
      if (typeof releaseNoteText === 'string') {
        data.release_note_content = releaseNoteText;
      } else if (isEdit && initialData?.release_note_content) {
        data.release_note_content = initialData.release_note_content;
      }

      // Add test reports (authoritative list; deletions = omission from the list)
      const testReportText = form.getFieldValue('test_report_content') as string;
      const trFilesAll = Array.isArray(uploadedFiles.test_report)
        ? uploadedFiles.test_report as UploadedFileWithType[]
        : uploadedFiles.test_report
          ? [uploadedFiles.test_report as UploadedFileWithType]
          : [];
      const trPendingDelete = capturedPendingDeletes.get('test_report') || [];
      const trDeletedKeys = new Set(trPendingDelete.map(f => f.fileUrl || f.fileName));
      // 以 DB 现有列表为底（避免 MultiFileUpload 取消删除标记时父组件状态里丢文件），
      // 去掉待删除项，再补上本次新增项
      const savedTrDocs = initialData?.test_report_files?.length
        ? initialData.test_report_files
        : (initialData?.test_report_url || initialData?.test_report_name)
          ? [{
              url: initialData.test_report_url || '',
              name: initialData.test_report_name || '',
              size: initialData.test_report_size || 0,
              checksum: initialData.test_report_checksum || '',
            }]
          : [];
      const trStateByKey = new Map(trFilesAll.map(f => [f.fileUrl || f.fileName, f]));
      const trSavedKeys = new Set(savedTrDocs.map(d => d.url || d.name));
      const trEffective: UploadedFileWithType[] = [];
      savedTrDocs.forEach((d) => {
        const key = d.url || d.name;
        if (!key || trDeletedKeys.has(key)) return;
        trEffective.push(trStateByKey.get(key) ?? {
          objectKey: '',
          fileUrl: d.url || '',
          fileName: d.name || '',
          fileSize: d.size || 0,
          md5: d.checksum || '',
          fileTypeId: 0,
          fileId: -1,
          sourceType: 'upload' as const,
        });
      });
      trFilesAll.forEach((f) => {
        const key = f.fileUrl || f.fileName;
        if (!trSavedKeys.has(key) && !trDeletedKeys.has(key)) trEffective.push(f);
      });
      data.test_report_files = trEffective.map(f => ({
        url: f.fileUrl || '',
        name: f.fileName || '',
        size: f.fileSize || 0,
        checksum: f.md5 || '',
      }));
      // 同 Release Note：文字内容以表单当前值为准，清空即清空
      if (typeof testReportText === 'string') {
        data.test_report_content = testReportText;
      } else if (isEdit && initialData?.test_report_content) {
        data.test_report_content = initialData.test_report_content;
      }

      // Include version for optimistic locking in edit mode
      if (isEdit && initialData?.version) {
        data.version = initialData.version as unknown as number;
      }

      // Strip rawFile before sending to API (not serializable)
      const apiData: PackageFormData = {
        ...data,
        files: data.files.map(({ rawFile: _rawFile, ...rest }) => rest),
      };

      // Attach deferred deletions to the PUT payload so backend merges into one log entry.
      // Artifact 行在 multi upsert 里按名字匹配，被删除/改名的旧行必须显式带上 id。
      if (isEdit) {
        const deletedFileIds: number[] = [];
        const deletedDocTypes: string[] = [];
        for (const [typeCode, filesToDelete] of capturedPendingDeletes) {
          if (typeCode === 'release_note') {
            // 测试报告改为整表提交，删除即从 test_report_files 中移除
            deletedDocTypes.push(typeCode);
          } else if (typeCode !== 'test_report') {
            for (const file of filesToDelete) {
              if (file.fileId) deletedFileIds.push(file.fileId);
            }
          }
        }
        for (const [code, olds] of Object.entries(initialArtifactFiles.current)) {
          const mode = firmwareModes[code] || 'upload';
          const currentNames = new Set(
            mode === 'artifact' ? (artifactInfo[code] || []).map((n) => n.trim()).filter(Boolean) : [],
          );
          for (const old of olds) {
            if (!currentNames.has(old.name.trim())) deletedFileIds.push(old.id);
          }
        }
        if (deletedFileIds.length > 0) apiData.deleted_file_ids = deletedFileIds;
        if (deletedDocTypes.length > 0) apiData.deleted_doc_types = deletedDocTypes;
      }

      const result = await onSubmit(apiData);
      message.success(t(isEdit ? '更新成功，文件正在后台上传中...' : '创建成功，文件正在后台上传中...'));

      // Start background uploads for deferred files
      const deferredFiles = data.files.filter(f => (f.upload_status === 'queued' || f.upload_status === 'uploading') && f.rawFile);
      const prioritySourceTypes = new Set(['select', 'git', 'artifact']);
      const priorityFiles = deferredFiles.filter(f => prioritySourceTypes.has(f.source_type ?? ''));
      const uploadFiles = deferredFiles
        .filter(f => !f.source_type || f.source_type === 'upload')
        .sort((a, b) => (a.file_size ?? 0) - (b.file_size ?? 0));

      // Also check deferred doc files (release_note, test_report)
      const deferredReleaseNote = releaseNoteDeferred ? (releaseNoteFile as UploadedFileWithType) : null;
      const deferredTestReports = trEffective
        .map((file, index) => ({ file, index }))
        .filter(({ file }) => !!file.rawFile && !file.fileUrl);

      const hasAnyDeferred = deferredFiles.length > 0 || deferredReleaseNote || deferredTestReports.length > 0;

      if (hasAnyDeferred && result?.id) {
        const packageId = result.id;
        const createdFiles = result.files || [];

        // Mark as uploading FIRST (awaited) so the status is correct before uploads start
        console.log('[PackageForm] 调用 updatePackageUploadStatus uploading, packageId=', packageId);
        await updatePackageUploadStatus(packageId, 'uploading').catch(() => {});
        console.log('[PackageForm] updatePackageUploadStatus uploading 完成');

        // Track failures so we can set the correct final status
        const failures: string[] = [];

        // Accumulate doc upload timing client-side so multiple doc uploads don't overwrite each other
        const currentDocTiming: Record<string, { started_at: string; finished_at: string; avg_speed_mbps?: number | null }> = {
          ...(initialData?.doc_upload_timing ?? {}),
        };

        // Semaphore: at most 1 file uploads at a time, others queue
        const MAX_CONCURRENT = 1;
        let semCount = 0;
        const semQueue: (() => void)[] = [];
        const semAcquire = () => new Promise<void>(resolve => {
          if (semCount < MAX_CONCURRENT) { semCount++; resolve(); }
          else semQueue.push(resolve);
        });
        const semRelease = () => {
          semCount--;
          const next = semQueue.shift();
          if (next) { semCount++; next(); }
        };

        const allUploads: Promise<void>[] = [];
        // Track deferred file metadata for upload record patch after all uploads complete
        const deferredFilesMeta: Array<{ key: string; file_name: string; file_size: number; file_type_name: string }> = [];

        // Helper to build snapshot of all files (not just deferred) for upload record
        const buildPackageAllFilesSnapshot = (packageId: number, createdFiles: Array<Record<string, any>>): UploadedFileItem[] => {
          const snapshot: UploadedFileItem[] = [];
          const store = useUploadProgressStore.getState();
          // Files already classified as 'existing' should not be re-added as 'added'
          const existingKeySet = new Set(
            (result?.categorizedFiles?.existing || []).map((f: any) => `${f.file_type_name ?? ''}::${f.file_name ?? ''}`)
          );
          createdFiles.forEach((f: any) => {
            const fileTypeName = (f.file_type?.name || fileTypes.find(ft => ft.id === (f.file_type_id ?? f.file_type?.id))?.name) || '';
            if (existingKeySet.has(`${fileTypeName}::${f.file_name ?? ''}`)) return;
            const key = `package-${packageId}-file-${f.id}`;
            const e = store.getProgress(key);
            const effectiveStatus = e?.status ?? 'done';
            snapshot.push({
              file_name: e?.rawFile?.name ?? f.file_name ?? '',
              file_size: e?.rawFile?.size ?? f.file_size ?? 0,
              file_type_name: fileTypeName,
              auto_retry_count: e?.autoRetryCount ?? 0,
              manual_retry_count: e?.manualRetryCount ?? 0,
              upload_started_at: e?.uploadStartedAt ?? null,
              upload_finished_at: e?.uploadFinishedAt ?? null,
              avg_speed_mbps: e?.avgSpeedMbps ?? null,
              change_type: effectiveStatus === 'failed' ? 'failed' : 'added',
            });
          });
          // Doc files — use file_size from deferredFilesMeta to avoid hardcoding 0
          const rnKey = `package-${packageId}-doc-release_note`;
          const rnEntry = store.getProgress(rnKey);
          const rnMeta = deferredFilesMeta.find(m => m.key === rnKey);
          if (rnEntry && rnMeta) {
            const effectiveStatus = rnEntry.status ?? 'done';
            snapshot.push({
              file_name: rnEntry.rawFile?.name ?? rnMeta.file_name,
              file_size: rnEntry.rawFile?.size ?? rnMeta.file_size,
              file_type_name: 'Release Note',
              auto_retry_count: rnEntry.autoRetryCount ?? 0,
              manual_retry_count: rnEntry.manualRetryCount ?? 0,
              upload_started_at: rnEntry.uploadStartedAt ?? null,
              upload_finished_at: rnEntry.uploadFinishedAt ?? null,
              avg_speed_mbps: rnEntry.avgSpeedMbps ?? null,
              change_type: effectiveStatus === 'failed' ? 'failed' : 'added',
            });
          }
          const trPrefix = `package-${packageId}-doc-test_report-`;
          deferredFilesMeta
            .filter(m => m.key.startsWith(trPrefix))
            .forEach((meta) => {
              const e = store.getProgress(meta.key);
              const effectiveStatus = e?.status ?? 'done';
              snapshot.push({
                file_name: e?.rawFile?.name ?? meta.file_name,
                file_size: e?.rawFile?.size ?? meta.file_size,
                file_type_name: 'Test Report',
                auto_retry_count: e?.autoRetryCount ?? 0,
                manual_retry_count: e?.manualRetryCount ?? 0,
                upload_started_at: e?.uploadStartedAt ?? null,
                upload_finished_at: e?.uploadFinishedAt ?? null,
                avg_speed_mbps: e?.avgSpeedMbps ?? null,
                change_type: effectiveStatus === 'failed' ? 'failed' : 'added',
              });
            });
          // Merge deleted and existing files from categorized data if available
          if (result?.categorizedFiles) {
            result.categorizedFiles.deleted.forEach((f: any) => snapshot.push({ ...f, change_type: 'deleted' }));
            result.categorizedFiles.existing.forEach((f: any) => snapshot.push({ ...f, change_type: 'existing' }));
          }
          return snapshot;
        };

        // Consumable list — each createdFile is spliced out once matched to prevent
        // multiple deferredFiles of the same file_type_id from mapping to the same DB record.
        const unmatchedCreated = createdFiles.filter(cf => cf.upload_status === 'queued' || cf.upload_status === 'uploading').slice();

        // Per-file upload function — called for each group in priority order
        const queueFileUpload = (fileData: (typeof deferredFiles)[0]) => {
          // Prefer exact file_name match; fall back to file_type_id.
          // splice() removes the matched entry so the next iteration can't reuse it.
          let matchIdx = unmatchedCreated.findIndex(cf => cf.file_name === fileData.file_name);
          if (matchIdx === -1) {
            matchIdx = unmatchedCreated.findIndex(cf =>
              (cf.file_type_id !== undefined && cf.file_type_id === fileData.file_type_id) ||
              (cf.file_type?.id !== undefined && cf.file_type.id === fileData.file_type_id)
            );
          }
          if (matchIdx === -1 || !fileData.rawFile) {
            // No matching DB record found — mark any unmatched created files as failed
            // to prevent them from staying 'uploading' forever in the DB.
            if (matchIdx === -1) {
              console.error(`[PackageForm] No DB record found for deferred file: ${fileData.file_name}, skipping upload`);
            }
            return;
          }
          const createdFile = unmatchedCreated.splice(matchIdx, 1)[0];

          const key = `package-${packageId}-file-${createdFile.id}`;
          const fileTypeName = fileTypes.find(ft => ft.id === (fileData.file_type_id ?? createdFile.file_type_id ?? createdFile.file_type?.id))?.name || '';
          deferredFilesMeta.push({ key, file_name: fileData.file_name, file_size: fileData.file_size || 0, file_type_name: fileTypeName });
          // 入队即标 queued（仅本地展示）；DB 里的初始态已是 queued（见提交载荷），
          // 这里不再写库，等拿到上传槽位时再置 uploading
          setProgress(key, { percent: 0, status: 'queued' });

          allUploads.push((async () => {
            // 心跳：每 1 分钟刷新 updated_at，防止其他 Tab 将此文件误判为卡住
            // currentPercent 在进度回调中无条件更新，确保定时器上报的是实时进度而非陈旧值
            let lastHeartbeatPercent = 0;
            let currentPercent = 0;
            const heartbeatTimer = setInterval(() => {
              lastHeartbeatPercent = currentPercent;
              heartbeatPackageFile(packageId, createdFile.id, currentPercent).catch(() => {});
            }, 1 * 60 * 1000);
            try {
              await semAcquire();
            } catch (err) {
              clearInterval(heartbeatTimer);
              throw err;
            }
            const raw = fileData.rawFile!;
            const startedAt = new Date().toISOString();
            // Slot acquired — switch to uploading in store and DB
            setProgress(key, { percent: 0, status: 'uploading', rawFile: raw, uploadStartedAt: startedAt, uploadFinishedAt: null, avgSpeedMbps: null });
            startPackageFileUpload(packageId, createdFile.id, 'uploading').catch(() => {});
            try {
              const uploadResult = await uploadFile(raw, (p) => {
                setProgress(key, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile: raw });
                currentPercent = p.percent;
                // 每涨 5% 向服务端写一次进度，让其他会话可感知（不随 setProgress 每次都写，避免过度请求）
                // 用差值比较而非 %10===0：进度可能跨过整十数，严格整除会导致此后永不上报
                if (p.percent > 0 && p.percent - lastHeartbeatPercent >= 5) {
                  lastHeartbeatPercent = p.percent;
                  heartbeatPackageFile(packageId, createdFile.id, p.percent).catch(() => {});
                }
              }, undefined, () => { incrementAutoRetry(key); }, key, (objectKey) => {
                heartbeatPackageFile(packageId, createdFile.id, 0, objectKey).catch(() => {});
              });
              const isUploadType = !fileData.source_type || fileData.source_type === 'upload';
              if (isUploadType && !uploadResult.md5) {
                console.error(`[PackageForm] MD5 empty for ${fileData.file_name}, marking as failed`);
                failures.push(fileData.file_name);
                setProgress(key, { percent: 0, status: 'failed', rawFile: raw, uploadFinishedAt: new Date().toISOString() });
                await failPackageFileUpload(packageId, createdFile.id).catch(() => {});
              } else {
                const finishedAt = new Date().toISOString();
                const durationSec = (new Date(finishedAt).getTime() - new Date(startedAt).getTime()) / 1000;
                const avgSpeedMbps = durationSec > 0 ? parseFloat((uploadResult.fileSize / 1024 / 1024 / durationSec).toFixed(2)) : null;
                setProgress(key, { percent: 100, status: 'done', uploadFinishedAt: finishedAt, avgSpeedMbps });
                if (result?.uploadRecordId) {
                  const snapshot = buildPackageAllFilesSnapshot(packageId, createdFiles);
                  await updateUploadRecord(result.uploadRecordId, {
                    uploaded_files: snapshot,
                    file_count: snapshot.filter(f => f.change_type !== 'deleted').length,
                    total_size: snapshot.filter(f => f.change_type !== 'deleted').reduce((s, f) => s + (f.file_size || 0), 0),
                  }).catch(() => {});
                }
                await retryCompleteUpload(async () => {
                  await completePackageFileUpload(packageId, createdFile.id, {
                    file_url: uploadResult.fileUrl,
                    object_key: uploadResult.objectKey,
                    file_size: uploadResult.fileSize,
                    checksum: uploadResult.md5 || '',
                    file_name: raw.name,
                    upload_started_at: startedAt,
                    upload_finished_at: finishedAt,
                    avg_speed_mbps: avgSpeedMbps,
                  });
                });
              }
            } catch (err) {
              const errMsg = err instanceof Error ? err.message : String(err);
              const isCancelled = errMsg.includes('cancelled') || errMsg.includes('aborted');
              setProgress(key, { percent: 0, status: 'failed', rawFile: raw, uploadFinishedAt: new Date().toISOString() });
              if (!isCancelled) {
                console.error(`[PackageForm] File upload failed: ${fileData.file_name}`, err);
                failures.push(fileData.file_name);
                reportUploadEvent('upload_error', { level: 'error', deliverable: 'package', deliverable_id: packageId, file_id: createdFile.id, file_name: fileData.file_name, detail: { error: errMsg } });
                await failPackageFileUpload(packageId, createdFile.id).catch(() => {});
              } else {
                reportUploadEvent('client_abort', { level: 'warn', deliverable: 'package', deliverable_id: packageId, file_id: createdFile.id, file_name: fileData.file_name, detail: { error: errMsg } });
              }
            } finally {
              clearInterval(heartbeatTimer);
              semRelease();
            }
          })());
        };

        // 1. select / git / artifact 文件优先（无二进制上传，处理快）
        priorityFiles.forEach(queueFileUpload);

        if (deferredReleaseNote?.rawFile) {
          const rnKey = `package-${packageId}-doc-release_note`;
          const rnRaw = deferredReleaseNote.rawFile!;
          deferredFilesMeta.push({ key: rnKey, file_name: deferredReleaseNote.fileName || rnRaw.name, file_size: rnRaw.size, file_type_name: 'Release Note' });
          console.log('[PackageForm] setProgress queued', rnKey);
          setProgress(rnKey, { percent: 0, status: 'queued', rawFile: rnRaw });
          allUploads.push((async () => {
            await semAcquire();
            const rnStartedAt = new Date().toISOString();
            setProgress(rnKey, { percent: 0, status: 'uploading', rawFile: rnRaw, uploadStartedAt: rnStartedAt, uploadFinishedAt: null, avgSpeedMbps: null });
            // 心跳：每 1 分钟刷新 package.updated_at，防止其他 Tab 将 ReleaseNote 误判为卡住
            const rnHeartbeatTimer = setInterval(() => {
              patchPackage(packageId, {}).catch(() => {});
            }, 1 * 60 * 1000);
            try {
              const r = await uploadFile(rnRaw, (p) => {
                setProgress(rnKey, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile: rnRaw });
              }, undefined, () => { incrementAutoRetry(rnKey); }, rnKey);
              const rnFinishedAt = new Date().toISOString();
              const rnDurationSec = (new Date(rnFinishedAt).getTime() - new Date(rnStartedAt).getTime()) / 1000;
              const rnAvgSpeedMbps = rnDurationSec > 0 ? parseFloat((r.fileSize / 1024 / 1024 / rnDurationSec).toFixed(2)) : null;
              setProgress(rnKey, { percent: 100, status: 'done', uploadFinishedAt: rnFinishedAt, avgSpeedMbps: rnAvgSpeedMbps });
              // Flush timing to UploadRecord BEFORE telling backend the file is done,
              // so _sync_upload_record_files sees complete timing when notification fires.
              if (result?.uploadRecordId) {
                const snapshot = buildPackageAllFilesSnapshot(packageId, createdFiles);
                await updateUploadRecord(result.uploadRecordId, {
                  uploaded_files: snapshot,
                  file_count: snapshot.filter(f => f.change_type !== 'deleted').length,
                  total_size: snapshot.filter(f => f.change_type !== 'deleted').reduce((s, f) => s + (f.file_size || 0), 0),
                }).catch(() => {});
              }
              currentDocTiming['release_note'] = { started_at: rnStartedAt, finished_at: rnFinishedAt, avg_speed_mbps: rnAvgSpeedMbps };
              await updatePackage(packageId, {
                release_note_url: r.fileUrl,
                release_note_name: r.fileName,
                release_note_size: r.fileSize,
                release_note_checksum: r.md5 || '',
                version: result.version || 1,
                force: true,
                skip_log: true,
                doc_upload_timing: { ...currentDocTiming },
              });
            } catch (err) {
              console.error('[PackageForm] Release note upload failed', err);
              failures.push('Release Note');
              setProgress(rnKey, { percent: 0, status: 'failed', rawFile: rnRaw, uploadFinishedAt: new Date().toISOString() });
              await failReleaseNoteUpload(packageId).catch(() => {});
            } finally {
              clearInterval(rnHeartbeatTimer);
              semRelease();
            }
          })());
        }

        // Upload deferred test reports (queued through semaphore like fixed files)
        const trUploadState = trEffective.map((file) => ({ ...file }));
        let trUploadFailed = false;
        const trUploadKey = (index: number) => `package-${packageId}-doc-test_report-${index}`;
        deferredTestReports.forEach(({ file, index }) => {
          const trRaw = file.rawFile!;
          const trKey = trUploadKey(index);
          deferredFilesMeta.push({ key: trKey, file_name: file.fileName || trRaw.name, file_size: trRaw.size, file_type_name: 'Test Report' });
          setProgress(trKey, { percent: 0, status: 'queued', rawFile: trRaw });
          allUploads.push((async () => {
            await semAcquire();
            const trStartedAt = new Date().toISOString();
            setProgress(trKey, { percent: 0, status: 'uploading', rawFile: trRaw, uploadStartedAt: trStartedAt, uploadFinishedAt: null, avgSpeedMbps: null });
            // 心跳：每 1 分钟刷新 package.updated_at，防止其他 Tab 将 TestReport 误判为卡住
            const trHeartbeatTimer = setInterval(() => {
              patchPackage(packageId, {}).catch(() => {});
            }, 1 * 60 * 1000);
            try {
              const r = await uploadFile(trRaw, (p) => {
                setProgress(trKey, { percent: p.percent, currentSpeed: p.currentSpeed, status: 'uploading', rawFile: trRaw });
              }, undefined, () => { incrementAutoRetry(trKey); }, trKey);
              const trFinishedAt = new Date().toISOString();
              const trDurationSec = (new Date(trFinishedAt).getTime() - new Date(trStartedAt).getTime()) / 1000;
              const trAvgSpeedMbps = trDurationSec > 0 ? parseFloat((r.fileSize / 1024 / 1024 / trDurationSec).toFixed(2)) : null;
              setProgress(trKey, { percent: 100, status: 'done', uploadFinishedAt: trFinishedAt, avgSpeedMbps: trAvgSpeedMbps });
              // Flush timing to UploadRecord BEFORE telling backend the file is done,
              // so _sync_upload_record_files sees complete timing when notification fires.
              if (result?.uploadRecordId) {
                const snapshot = buildPackageAllFilesSnapshot(packageId, createdFiles);
                await updateUploadRecord(result.uploadRecordId, {
                  uploaded_files: snapshot,
                  file_count: snapshot.filter(f => f.change_type !== 'deleted').length,
                  total_size: snapshot.filter(f => f.change_type !== 'deleted').reduce((s, f) => s + (f.file_size || 0), 0),
                }).catch(() => {});
              }
              trUploadState[index] = {
                ...trUploadState[index],
                fileUrl: r.fileUrl,
                fileName: r.fileName || trUploadState[index].fileName,
                fileSize: r.fileSize,
                md5: r.md5 || '',
                rawFile: undefined,
                uploadKey: trKey,
              };
              currentDocTiming['test_report'] = { started_at: trStartedAt, finished_at: trFinishedAt, avg_speed_mbps: trAvgSpeedMbps };
              await updatePackage(packageId, {
                test_report_files: trUploadState.map((f) => ({
                  url: f.fileUrl || '',
                  name: f.fileName || '',
                  size: f.fileSize || 0,
                  checksum: f.md5 || '',
                })),
                doc_files_merge: true,
                version: result.version || 1,
                force: true,
                skip_log: true,
                doc_upload_timing: { ...currentDocTiming },
              });
            } catch (err) {
              console.error('[PackageForm] Test report upload failed', err);
              trUploadFailed = true;
              setProgress(trKey, { percent: 0, status: 'failed', rawFile: trRaw, uploadFinishedAt: new Date().toISOString() });
            } finally {
              clearInterval(trHeartbeatTimer);
              semRelease();
            }
          })());
        });

        // 4. upload 类型文件，按文件大小升序（小文件先传）
        uploadFiles.forEach(queueFileUpload);

        Promise.all(allUploads).then(async () => {
          if (trUploadFailed) {
            failures.push('测试报告');
            // 成功文件的回写 PATCH 会把状态重算成「上传中」，最后再统一置 failed
            await failTestReportUpload(packageId).catch(() => {});
          }
          const finalStatus = failures.length > 0 ? 'failed' : 'done';
          if (result?.uploadRecordId) {
            const allFiles = buildPackageAllFilesSnapshot(packageId, createdFiles);
            const nonDeletedFiles = allFiles.filter(f => f.change_type !== 'deleted');
            await updateUploadRecord(result.uploadRecordId, {
              upload_status: finalStatus,
              finished_at: new Date().toISOString(),
              uploaded_files: allFiles,
              file_count: nonDeletedFiles.length,
              total_size: nonDeletedFiles.reduce((s, f) => s + (f.file_size || 0), 0),
            }).catch(() => {});
          }
          updatePackageUploadStatus(
            packageId, finalStatus,
            failures.length > 0 ? `files_failed:${failures.join(',')}` : 'all_files_done'
          ).catch(() => {});
          if (failures.length > 0) {
            import('antd').then(({ message: msg }) => {
              msg.error(t('以下文件上传失败，请编辑重试：{files}', { files: failures.map((f) => t(f)).join(lang === 'en' ? ', ' : '、') }));
            });
          }
        }).catch(async (err) => {
          console.error('[PackageForm] Unexpected error while awaiting uploads:', err);
          if (result?.uploadRecordId) {
            await updateUploadRecord(result.uploadRecordId, {
              upload_status: 'failed',
              finished_at: new Date().toISOString(),
            }).catch(() => {});
          }
          updatePackageUploadStatus(packageId, 'failed', `unexpected_exception:${String(err)}`).catch(() => {});
        });
      } else if (result?.id) {
        if (result.uploadRecordId) {
          const noUploadFiles: UploadedFileItem[] = [];
          if (result.categorizedFiles) {
            result.categorizedFiles.deleted.forEach((f: any) => noUploadFiles.push({ ...f, change_type: 'deleted' }));
            result.categorizedFiles.existing.forEach((f: any) => noUploadFiles.push({ ...f, change_type: 'existing' }));
          }
          updateUploadRecord(result.uploadRecordId, {
            upload_status: 'done',
            finished_at: new Date().toISOString(),
            ...(noUploadFiles.length > 0 ? {
              uploaded_files: noUploadFiles,
              file_count: noUploadFiles.filter(f => f.change_type !== 'deleted').length,
              total_size: noUploadFiles.filter(f => f.change_type !== 'deleted').reduce((s, f) => s + (f.file_size || 0), 0),
            } : {}),
          }).catch(() => {});
        }
        updatePackageUploadStatus(result.id, 'done').catch(() => {});
      }
    } catch (error) {
      const err = error as { response?: { status: number; data?: any } };

      // DEBUG: Log full error response
      console.error('[PackageForm:Submit:Error] Full error response:', {
        status: err.response?.status,
        data: err.response?.data,
        dataType: typeof err.response?.data,
        dataKeys: err.response?.data ? Object.keys(err.response.data) : [],
      });

      if (err.response?.status === 409) {
        message.error(t('数据已被其他用户修改，请刷新后重试'));
      } else {
        // Try to extract error message from various possible formats
        let errorMsg = t(isEdit ? '更新失败' : '创建失败');

        if (err.response?.data) {
          if (typeof err.response.data === 'string') {
            errorMsg = err.response.data;
          } else if (err.response.data.detail) {
            errorMsg = err.response.data.detail;
          } else if (err.response.data.message) {
            errorMsg = err.response.data.message;
          } else if (err.response.data.error) {
            errorMsg = err.response.data.error;
          } else {
            // If it's an object with validation errors, format them
            errorMsg = JSON.stringify(err.response.data);
          }
        }

        message.error(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // 编辑模式：仅当有实际变更时才允许点击"更新"
  const hasChanges = !isEdit ? true : (() => {
    if (!initialSnapshot.current) return false;
    const snap = initialSnapshot.current;

    // firmwareModes 变化（新增/删除/切换模式）
    if (JSON.stringify(firmwareModes) !== JSON.stringify(snap.firmwareModes)) return true;

    // gitInfo 变化
    if (JSON.stringify(gitInfo) !== JSON.stringify(snap.gitInfo)) return true;

    // artifactInfo 变化
    if (JSON.stringify(artifactInfo) !== JSON.stringify(snap.artifactInfo)) return true;

    // Git/Artifact 备注变化
    if (JSON.stringify(remarkInfo) !== JSON.stringify(snap.remarkInfo)) return true;

    // pendingDeleteFiles 有内容（有文件待删除）
    if (pendingDeleteFiles.size > 0) return true;

    // deletedDocs 有内容（文档已删除）
    if (deletedDocs.size > 0) return true;

    // 普通上传文件变化（用 fileId/fileUrl + 备注集合对比，排除文档字段）
    const fileIds = (files: FormFiles) =>
      Object.entries(files)
        .filter(([k]) => !['release_note', 'test_report', 'momenta_release_note', 'momenta_test_report'].includes(k))
        .flatMap(([, v]) => Array.isArray(v) ? v : (v ? [v] : []))
        .map((f) => {
          const file = f as UploadedFileWithType;
          return `${file.fileId ?? (f as UploadedFile).fileUrl}|${file.remark ?? ''}`;
        })
        .sort()
        .join(',');
    if (fileIds(uploadedFiles) !== fileIds(snap.uploadedFiles)) return true;

    // release note 文件变化
    const fileKey = (f: UploadedFile | UploadedFileWithType | UploadedFileWithType[] | UploadedFile[] | undefined) => {
      if (!f || Array.isArray(f)) return '';
      return (f as UploadedFileWithType).fileId != null
        ? String((f as UploadedFileWithType).fileId)
        : (f as UploadedFile).fileUrl ?? (f as UploadedFile).fileName ?? '';
    };
    if (fileKey(uploadedFiles.release_note) !== fileKey(snap.uploadedFiles.release_note)) return true;
    const trFilesKey = (f: FormFiles['test_report']) => Array.isArray(f)
      ? f.map((item) => (item as UploadedFileWithType).fileUrl || (item as UploadedFileWithType).fileName || '').sort().join('|')
      : fileKey(f);
    if (trFilesKey(uploadedFiles.test_report) !== trFilesKey(snap.uploadedFiles.test_report)) return true;

    // release note / test report 内容变化
    const currRnContent = (form.getFieldValue('release_note_content') as string) ?? '';
    const currTrContent = (form.getFieldValue('test_report_content') as string) ?? '';
    if (currRnContent !== snap.releaseNoteContent) return true;
    if (currTrContent !== snap.testReportContent) return true;

    // 文档模式切换
    if (releaseNoteMode !== snap.releaseNoteMode) return true;
    if (testReportMode !== snap.testReportMode) return true;

    // external_release_ids 变化
    const currIds = ((form.getFieldValue('external_release_ids') as number[]) ?? []).slice().sort().join(',');
    const snapIds = snap.externalReleaseIds.slice().sort().join(',');
    if (currIds !== snapIds) return true;

    // name / 车型 / 子车型 / 硬件平台 / description 变化
    if (((form.getFieldValue('name') as string) ?? '') !== snap.name) return true;
    if (vehicleListKey(form.getFieldValue('vehicle_models') as string[] | undefined) !== snap.vehicleModels) return true;
    if (vehicleListKey(form.getFieldValue('vehicle_subtypes') as string[] | undefined) !== snap.vehicleSubtypes) return true;
    if (vehicleListKey(form.getFieldValue('platforms') as string[] | undefined) !== snap.platforms) return true;
    if (((form.getFieldValue('description') as string) ?? '') !== snap.description) return true;

    return false;
  })();

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onValuesChange={() => { if (isEdit) forceUpdate(n => n + 1); }}
      initialValues={{
        name: '',
        vehicle_models: [],
        vehicle_subtypes: [],
        platforms: [],
        description: '',
        release_note_content: '',
        test_report_content: '',
      }}
    >
      <Card title={t('基本信息')} className="mb-4" styles={{ body: { padding: '12px 16px' } }}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-2">
          {/* First row: 包名称(6) + 车型(2) + 子车型(2) + 硬件平台(2) */}
          <Form.Item
            name="name"
            label={t('底软包名称')}
            rules={[{ required: true, message: t('请输入底软包名称') }]}
            className="mb-0 md:col-span-6"
          >
            <Input placeholder={t('输入底软包名称')} />
          </Form.Item>

          <Form.Item
            name="vehicle_models"
            label={t('车型')}
            className="mb-0 md:col-span-2"
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('选择车型')}
              options={vehicleTypeOptions}
            />
          </Form.Item>

          <Form.Item
            name="vehicle_subtypes"
            label={t('子车型')}
            className="mb-0 md:col-span-2"
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={vehicleModelsWatch.length ? t('选择子车型（可选）') : t('请先选择车型')}
              disabled={!vehicleModelsWatch.length}
              options={vehicleSubtypeOptions}
            />
          </Form.Item>

          <Form.Item
            name="platforms"
            label={t('硬件平台')}
            className="mb-0 md:col-span-2"
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t('选择硬件平台（可选）')}
              options={platformOptions}
            />
          </Form.Item>

          {/* Second row: 关联释放产物包(6) + 说明(6) */}
          <Form.Item
            name="external_release_ids"
            label={t('关联Momenta释放产物包')}
            className="mb-0 md:col-span-6"
          >
            <Select
              mode="multiple"
              placeholder={t('请选择关联的Momenta释放产物包（可多选，选填）')}
              allowClear
              optionFilterProp="label"
              options={availableExternalReleases.map((r) => ({ value: r.id, label: r.name }))}
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('说明')}
            className="mb-0 md:col-span-6"
          >
            <TextArea
              placeholder={t('输入底软包说明信息')}
              rows={2}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </div>
      </Card>

      {/* 底软供应商上传文件 */}
      <Card title={t('底软供应商上传文件')} className="mb-4" styles={{ body: { padding: '12px 16px' } }}>
        {/* 固定文件（固件） */}
        <Divider titlePlacement="start" styles={{ content: { margin: 0 } }} style={{ margin: '8px 0' }}>
          <span style={{ fontWeight: 'bold' }}>{t('固定文件')}</span>
        </Divider>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {supplierFileTypes.map((fileType) => {
            const mode = firmwareModes[fileType.code] || 'upload';

            return (
              <div key={fileType.code}>
                <div className="mb-1">
                  <label className="font-medium block mb-1">
                    {fileType.name}
                    {fileType.description && (
                      <Tooltip title={fileType.description}>
                        <QuestionCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c', fontSize: 12, cursor: 'help' }} />
                      </Tooltip>
                    )}
                  </label>
                  <Radio.Group
                    value={mode}
                    onChange={(e) => {
                      const newMode = e.target.value as 'upload' | 'git' | 'artifact';
                      setFirmwareModes((prev) => ({ ...prev, [fileType.code]: newMode }));
                      // Pre-fill git URL and branch from previous packages when switching to git mode
                      if (newMode === 'git' && previousGitSources?.length) {
                        const src = previousGitSources.find(s => s.file_type_code === fileType.code);
                        if (src && (src.git_url || src.git_branch) && !gitInfo[fileType.code]) {
                          setGitInfo((prev) => ({
                            ...prev,
                            [fileType.code]: { url: src.git_url, branch: src.git_branch, commit: '', tag: '' },
                          }));
                        }
                      }
                    }}
                    size="small"
                  >
                    {(() => {
                      return (
                        <>
                          <Radio.Button value="upload">
                            {t('上传')}
                          </Radio.Button>
                          <Radio.Button value="git">
                            Git
                          </Radio.Button>
                          <Radio.Button value="artifact">
                            Artifact
                          </Radio.Button>
                        </>
                      );
                    })()}
                  </Radio.Group>
                </div>
                {mode === 'git' ? (
                  <div className="space-y-2">
                    <Input
                      placeholder={t('Git仓库URL (必填)')}
                      value={gitInfo[fileType.code]?.url || ''}
                      onChange={(e) => {
                        setGitInfo((prev) => ({
                          ...prev,
                          [fileType.code]: {
                            ...prev[fileType.code],
                            url: e.target.value,
                            branch: prev[fileType.code]?.branch || '',
                            commit: prev[fileType.code]?.commit || '',
                            tag: prev[fileType.code]?.tag || '',
                          },
                        }));
                      }}
                    />
                    <Input
                      placeholder={t('Git分支 (必填)')}
                      value={gitInfo[fileType.code]?.branch || ''}
                      onChange={(e) => {
                        setGitInfo((prev) => ({
                          ...prev,
                          [fileType.code]: {
                            ...prev[fileType.code],
                            url: prev[fileType.code]?.url || '',
                            branch: e.target.value,
                            commit: prev[fileType.code]?.commit || '',
                            tag: prev[fileType.code]?.tag || '',
                          },
                        }));
                      }}
                    />
                    {previousGitSources?.some(s => s.file_type_code === fileType.code) &&
                     !initialData?.files?.some(f => f.file_type?.code === fileType.code && f.source_type === 'git') && (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
                        {t('URL和分支已从上一版本自动填写')}
                      </Text>
                    )}
                    <Input
                      placeholder={t('Git Commit ID (与Tag二选一)')}
                      value={gitInfo[fileType.code]?.commit || ''}
                      onChange={(e) => {
                        setGitInfo((prev) => ({
                          ...prev,
                          [fileType.code]: {
                            ...prev[fileType.code],
                            url: prev[fileType.code]?.url || '',
                            branch: prev[fileType.code]?.branch || '',
                            commit: e.target.value,
                            tag: prev[fileType.code]?.tag || '',
                          },
                        }));
                      }}
                    />
                    <Input
                      placeholder={t('Git Tag (与Commit二选一)')}
                      value={gitInfo[fileType.code]?.tag || ''}
                      onChange={(e) => {
                        setGitInfo((prev) => ({
                          ...prev,
                          [fileType.code]: {
                            ...prev[fileType.code],
                            url: prev[fileType.code]?.url || '',
                            branch: prev[fileType.code]?.branch || '',
                            commit: prev[fileType.code]?.commit || '',
                            tag: e.target.value,
                          },
                        }));
                      }}
                    />
                    <Input
                      placeholder={t('备注（选填）')}
                      maxLength={500}
                      allowClear
                      value={remarkInfo[fileType.code] ?? ''}
                      onChange={(e) => {
                        setRemarkInfo((prev) => ({ ...prev, [fileType.code]: e.target.value }));
                      }}
                    />
                    <Button
                      danger
                      type="link"
                      size="small"
                      onClick={() => handleClearGit(fileType.code)}
                      style={{ marginTop: '4px', display: 'block' }}
                    >
                      {t('清空Git配置')}
                    </Button>
                  </div>
                ) : mode === 'artifact' ? (
                  <div className="space-y-2">
                    {isMultiFileType(fileType) ? (
                      <>
                        {(artifactInfo[fileType.code]?.length ? artifactInfo[fileType.code] : ['']).map((name, index) => (
                          <Space.Compact key={index} style={{ width: '100%' }}>
                            <Input
                              placeholder={t('Artifact产物包名称 (必填)')}
                              value={name}
                              onChange={(e) => setArtifactRow(fileType.code, index, e.target.value)}
                            />
                            {(artifactInfo[fileType.code]?.length || 1) > 1 && (
                              <Button
                                icon={<DeleteOutlined />}
                                onClick={() => removeArtifactRow(fileType.code, index)}
                                aria-label={t('删除该 Artifact')}
                              />
                            )}
                          </Space.Compact>
                        ))}
                        <Button
                          type="dashed"
                          icon={<PlusOutlined />}
                          onClick={() => addArtifactRow(fileType.code)}
                          block
                        >
                          {t('添加 Artifact')}
                        </Button>
                      </>
                    ) : (
                      <Input
                        placeholder={t('Artifact产物包名称 (必填)')}
                        value={artifactInfo[fileType.code]?.[0] || ''}
                        onChange={(e) => setArtifactRow(fileType.code, 0, e.target.value)}
                      />
                    )}
                    <Input
                      placeholder={t('备注（选填）')}
                      maxLength={500}
                      allowClear
                      value={remarkInfo[fileType.code] ?? ''}
                      onChange={(e) => {
                        setRemarkInfo((prev) => ({ ...prev, [fileType.code]: e.target.value }));
                      }}
                    />
                  </div>
                ) : (
                  <>
                    {/* 关键改动：根据配置决定使用单文件还是多文件组件 */}
                    {isMultiFileType(fileType) ? (
                      <MultiFileUpload
                        fileType={fileType.code}
                        existingFiles={uploadedFiles[fileType.code] as UploadedFileWithType[] | undefined}
                        onFilesChange={(files) => {
                          // 确保每个文件都设置了正确的fileTypeId和sourceType
                          const filesWithTypeId = files.map(file => ({
                            ...file,
                            fileTypeId: fileType.id,
                            // sourceType已经由MultiFileUpload内部设置，保持不变
                          }));

                          setUploadedFiles((prev) => ({
                            ...prev,
                            [fileType.code]: filesWithTypeId.length > 0 ? filesWithTypeId : undefined,
                          }));
                        }}
                        uploadConfig={{}}
                        disabled={submitting}
                        deferUpload={true}
                        showRemark={true}
                        allowSelectExisting={true}
                        projectFiles={projectFilesCache[fileType.code] || []}
                        loadingProjectFiles={loadingFiles[fileType.code]}
                        onLoadProjectFiles={() => fetchFilesForType(fileType.code, fileType.id)}
                        onRetry={isEdit ? handleFormRetry : undefined}
                        onDelete={isEdit && initialData?.id ? async (file) => {
                          console.log('[PackageForm:onDelete:multi] file=', { fileId: file.fileId, fileName: file.fileName, fileUrl: file.fileUrl, file_source: (file as any).file_source, fileTypeCode: fileType.code });
                          try {
                            await deletePackageFile(initialData.id, file.fileId!, file.file_source === 'momenta' ? 'momenta' : 'supplier');
                            message.success(t('文件已删除'));
                          } catch (e: any) {
                            const msg = e?.response?.data?.detail || e?.message || t('删除失败');
                            message.error(msg);
                            console.error('[PackageForm:onDelete] error:', e);
                            return;
                          }
                          setUploadedFiles((prev) => {
                            const current = prev[fileType.code];
                            console.log('[PackageForm:onDelete:multi] before filter, current=', current);
                            if (Array.isArray(current)) {
                              const filtered = (current as UploadedFileWithType[]).filter(f => f.fileId !== file.fileId);
                              console.log('[PackageForm:onDelete:multi] after filter, filtered=', filtered);
                              return { ...prev, [fileType.code]: filtered.length > 0 ? filtered : undefined };
                            }
                            return { ...prev, [fileType.code]: undefined };
                          });
                        } : undefined}
                        deferDelete={!!isEdit}
                        onPendingDeleteChange={(pendingFiles) => {
                          setPendingDeleteFiles(prev => {
                            const next = new Map(prev);
                            next.set(fileType.code, pendingFiles);
                            return next;
                          });
                        }}
                      />
                    ) : (
                      <MultiFileUpload
                        fileType={fileType.code}
                        existingFiles={
                          uploadedFiles[fileType.code]
                            ? Array.isArray(uploadedFiles[fileType.code])
                              ? (uploadedFiles[fileType.code] as UploadedFileWithType[])
                              : [{
                                  ...(uploadedFiles[fileType.code] as UploadedFileWithType),
                                  fileTypeId: fileType.id,
                                } as UploadedFileWithType]
                            : undefined
                        }
                        onFilesChange={(files) => {
                          const filesWithTypeId = files.map(file => ({
                            ...file,
                            fileTypeId: fileType.id,
                          }));
                          // 单文件模式：只保存第一个文件作为单对象
                          const saved = filesWithTypeId.length > 0 ? filesWithTypeId[0] : undefined;
                          console.log('[PackageForm:onFilesChange:single] typeCode=', fileType.code, 'incoming files=', files.map(f => ({ fileName: f.fileName, fileId: f.fileId })), 'saving=', saved ? { fileName: saved.fileName, fileId: saved.fileId } : null);
                          setUploadedFiles((prev) => ({
                            ...prev,
                            [fileType.code]: saved,
                          }));
                        }}
                        uploadConfig={{}}
                        disabled={submitting}
                        maxFiles={1}  // 单文件模式：限制最多1个文件
                        deferUpload={true}
                        showRemark={true}
                        allowSelectExisting={true}
                        projectFiles={projectFilesCache[fileType.code] || []}
                        loadingProjectFiles={loadingFiles[fileType.code]}
                        onLoadProjectFiles={() => fetchFilesForType(fileType.code, fileType.id)}
                        onRetry={isEdit ? handleFormRetry : undefined}
                        onDelete={isEdit && initialData?.id ? async (file) => {
                          await deletePackageFile(initialData.id, file.fileId!, file.file_source === 'momenta' ? 'momenta' : 'supplier');
                          setUploadedFiles((prev) => ({ ...prev, [fileType.code]: undefined }));
                        } : undefined}
                        deferDelete={!!isEdit}
                        onPendingDeleteChange={(pendingFiles) => {
                          setPendingDeleteFiles(prev => {
                            const next = new Map(prev);
                            next.set(fileType.code, pendingFiles);
                            return next;
                          });
                        }}
                      />
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* 供应商文档文件 */}
        <Divider titlePlacement="start" styles={{ content: { margin: 0 } }} style={{ margin: '8px 0' }}>
          <span style={{ fontWeight: 'bold' }}>{t('文档文件')}</span>
        </Divider>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Release Note */}
          <div>
            <div className="mb-3">
              <label className="font-medium block mb-2">
                Release Note <span className="text-red-500">*</span>
              </label>
              <Radio.Group
                value={releaseNoteMode}
                onChange={(e) => setReleaseNoteMode(e.target.value)}
                size="small"
              >
                <Radio.Button value="upload">{t('上传')}</Radio.Button>
                <Radio.Button value="input">{t('输入')}</Radio.Button>
              </Radio.Group>

              {/* NEW: Show hint when in edit mode with both content and URL */}
              {isEdit && (
                <div className="text-xs text-gray-500 mt-1">
                  {releaseNoteMode === 'upload' && initialData?.release_note_content && (
                    <span>{t('ℹ️ 已有文本内容将被保留')}</span>
                  )}
                  {releaseNoteMode === 'input' && initialData?.release_note_url && (
                    <span>{t('ℹ️ 已有附件将被保留')}</span>
                  )}
                </div>
              )}
            </div>
            {/* 始终挂载，输入模式时隐藏，以保留上传进度和已上传文件状态 */}
            <div style={{ display: releaseNoteMode === 'upload' ? undefined : 'none' }}>
              {(() => {
                const releaseNoteType = fileTypes.find(ft => ft.code === 'release_note');
                if (releaseNoteType && isMultiFileType(releaseNoteType)) {
                  // 多文件上传模式
                  return (
                    <MultiFileUpload
                      fileType="release_note"
                      existingFiles={
                        uploadedFiles.release_note
                          ? Array.isArray(uploadedFiles.release_note)
                            ? (uploadedFiles.release_note as UploadedFileWithType[])
                            : [{
                                ...uploadedFiles.release_note,
                                fileTypeId: releaseNoteType.id,
                                sourceType: 'upload' as const,
                              } as UploadedFileWithType]
                          : undefined
                      }
                      onFilesChange={(files) => {
                        const filesWithTypeId = files.map(file => ({
                          ...file,
                          fileTypeId: releaseNoteType.id,
                          // Keep original sourceType from the file
                        }));
                        setUploadedFiles((prev) => ({
                          ...prev,
                          release_note: filesWithTypeId.length > 0 ? filesWithTypeId : undefined,
                        }));
                      }}
                      uploadConfig={{}}
                      disabled={submitting}
                      deferUpload={true}
                      onRetry={isEdit ? (_, rawFile) => handleFormDocRetry('release_note', rawFile) : undefined}
                      onDelete={isEdit && initialData?.id ? async (_file) => {
                        await deleteReleaseNote(initialData.id);
                        setDeletedDocs(prev => new Set([...prev, 'release_note']));
                      } : undefined}
                      deferDelete={!!isEdit}
                      onPendingDeleteChange={(pendingFiles) => {
                        setPendingDeleteFiles(prev => {
                          const next = new Map(prev);
                          next.set('release_note', pendingFiles);
                          return next;
                        });
                      }}
                    />
                  );
                } else {
                  // 单文件上传模式：使用MultiFileUpload with maxFiles=1
                  const releaseNoteType = fileTypes.find(ft => ft.code === 'release_note');
                  return (
                    <MultiFileUpload
                      fileType="release_note"
                      existingFiles={
                        uploadedFiles.release_note
                          ? Array.isArray(uploadedFiles.release_note)
                            ? (uploadedFiles.release_note as UploadedFileWithType[])
                            : [{
                                ...(uploadedFiles.release_note as UploadedFile),
                                fileTypeId: releaseNoteType?.id || 0,
                                sourceType: 'upload' as const,
                              } as UploadedFileWithType]
                          : undefined
                      }
                      onFilesChange={(files) => {
                        const filesWithTypeId = files.map(file => ({
                          ...file,
                          fileTypeId: releaseNoteType?.id || 0,
                          // Keep original sourceType from the file
                        }));
                        setUploadedFiles((prev) => ({
                          ...prev,
                          release_note: filesWithTypeId.length > 0 ? filesWithTypeId[0] : undefined,
                        }));
                      }}
                      uploadConfig={{}}
                      disabled={submitting}
                      maxFiles={1}
                      deferUpload={true}
                      onRetry={isEdit ? (_, rawFile) => handleFormDocRetry('release_note', rawFile) : undefined}
                      onDelete={isEdit && initialData?.id ? async (_file) => {
                        await deleteReleaseNote(initialData.id);
                        setDeletedDocs(prev => new Set([...prev, 'release_note']));
                      } : undefined}
                      deferDelete={!!isEdit}
                      onPendingDeleteChange={(pendingFiles) => {
                        setPendingDeleteFiles(prev => {
                          const next = new Map(prev);
                          next.set('release_note', pendingFiles);
                          return next;
                        });
                      }}
                    />
                  );
                }
              })()}
            </div>
            {releaseNoteMode === 'input' && (
              <Form.Item
                name="release_note_content"
                rules={[{ required: true, message: t('请输入 Release Note 内容') }]}
              >
                <TextArea rows={6} placeholder={t('输入 Release Note 内容')} />
              </Form.Item>
            )}
          </div>

          {/* 测试报告 */}
          <div>
            <div className="mb-3">
              <label className="font-medium block mb-2">
                {t('测试报告')} <span className="text-red-500">*</span>
              </label>
              <Radio.Group
                value={testReportMode}
                onChange={(e) => setTestReportMode(e.target.value)}
                size="small"
              >
                <Radio.Button value="upload">{t('上传')}</Radio.Button>
                <Radio.Button value="input">{t('输入')}</Radio.Button>
              </Radio.Group>

              {/* NEW: Show hint when in edit mode with both content and URL */}
              {isEdit && (
                <div className="text-xs text-gray-500 mt-1">
                  {testReportMode === 'upload' && initialData?.test_report_content && (
                    <span>{t('ℹ️ 已有文本内容将被保留')}</span>
                  )}
                  {testReportMode === 'input' && initialData?.test_report_url && (
                    <span>{t('ℹ️ 已有附件将被保留')}</span>
                  )}
                </div>
              )}
            </div>
            {/* 始终挂载，输入模式时隐藏，以保留上传进度和已上传文件状态 */}
            <div style={{ display: testReportMode === 'upload' ? undefined : 'none' }}>
              <MultiFileUpload
                fileType="test_report"
                existingFiles={
                  Array.isArray(uploadedFiles.test_report)
                    ? (uploadedFiles.test_report as UploadedFileWithType[])
                    : uploadedFiles.test_report
                      ? [{ ...(uploadedFiles.test_report as UploadedFileWithType), sourceType: 'upload' as const }]
                      : undefined
                }
                onFilesChange={(files) => {
                  setUploadedFiles((prev) => ({
                    ...prev,
                    test_report: files.length > 0 ? files : undefined,
                  }));
                }}
                uploadConfig={{}}
                disabled={submitting}
                deferUpload={true}
                onRetry={isEdit ? (file, rawFile) => handleFormDocRetry('test_report', rawFile, file) : undefined}
                deferDelete={!!isEdit}
                onPendingDeleteChange={(pendingFiles) => {
                  setPendingDeleteFiles(prev => {
                    const next = new Map(prev);
                    next.set('test_report', pendingFiles);
                    return next;
                  });
                }}
              />
            </div>
            {testReportMode === 'input' && (
              <Form.Item
                name="test_report_content"
                rules={[{ required: true, message: t('请输入测试报告内容') }]}
              >
                <TextArea rows={6} placeholder={t('输入测试报告内容')} />
              </Form.Item>
            )}
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-2">
        <Space>
          <Button
            icon={<CloseOutlined />}
            onClick={onCancel}
          >
            {t('取消')}
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            htmlType="submit"
            loading={loading || submitting}
            disabled={!hasChanges}
          >
            {isEdit ? t('更新') : t('提交')}
          </Button>
        </Space>
      </div>

      {/* File Type Change Modal */}
      <Modal
        title={t('修改文件类型')}
        open={!!editingFileKey}
        onCancel={() => {
          setEditingFileKey(null);
          setSelectedNewFileTypeId(null);
        }}
        onOk={handleUpdateFileType}
        okText={t('确认')}
        cancelText={t('取消')}
        confirmLoading={updatingFileKey !== null}
      >
        {editingFileKey && uploadedFiles[editingFileKey] && (
          <Space orientation="vertical" style={{ width: '100%' }}>
            <div>
              <strong>{t('文件名：')}</strong> {(uploadedFiles[editingFileKey] as UploadedFile).fileName}
            </div>
            <div>
              <strong>{t('当前类型：')}</strong> {fileTypes.find((ft) => ft.code === editingFileKey)?.name || editingFileKey}
            </div>
            <div>
              <strong>{t('新类型：')}</strong>
              <Select
                value={selectedNewFileTypeId}
                onChange={setSelectedNewFileTypeId}
                style={{ width: '100%', marginTop: '8px' }}
                placeholder={t('选择新的文件类型')}
                options={fileTypes
                  .filter((ft) => ft.code !== 'release_note' && ft.code !== 'test_report')
                  .map((ft) => ({ value: ft.id, label: ft.name }))}
              />
            </div>
          </Space>
        )}
      </Modal>
    </Form>
  );
};

export default PackageForm;
